import {
  ADD_F9_NOTE,
  BULK_TRANSFORM_LINE_ITEMS,
  COMPLEMENTS_AND_RECOMMENDATIONS,
  DEFAULT_QUANTITIES,
  DayOfWeek,
  EDrywallWidthCalculation,
  EExtraDataMappingType,
  EExtraVariableValues,
  ESTIMATE_TYPE_LABELS,
  ESTIMATE_TYPE_TO_TAGS,
  ESkipProcessingReason,
  EstimateCreationSource,
  EstimateFieldsForExportHeader,
  EstimateReferenceNumberType,
  EstimateStatus,
  EstimateType,
  EstimaticsFileTypes,
  FileErrorDummyPath,
  GENERATE_FROM_ESTIMATE_FIELD,
  GeneralRoomOrder,
  Holiday,
  INCLUDE_OR_EXCLUDE_VALUES,
  INHERIT_FORMULA,
  ISSUE_CONFIDENCE_THRESHOLD,
  Kind,
  LINE_ITEM_TRANSFORMATION,
  LineItemCategory,
  MERGE_COMPLEMENTS,
  MULTI_PARENT_RECOMMENDATIONS,
  MULTI_PARENT_TRANSFORMATIONS,
  MUTUALLY_EXCLUSIVE,
  NOTE_TYPE_TO_ESTIMATE_TYPE,
  NOTE_TYPE_VALUES,
  NoteFlowType,
  NoteType,
  ONE_LINE_ITEM_UPDATES_ANOTHER,
  PAINT_AFFECTED_AND_CONTINUOUS_SURFACE,
  PCWidthCalculation,
  PFWidthCalculation,
  PdfFileFormat,
  QueuedItemToProcessStatus,
  RULE_RUN_CRITERIA,
  RULE_TEMPLATE_IDS,
  ResponsiveNoteTemplateVisitType,
  RoomType,
  RuleQueryFieldType,
  SPLIT_LINE_ITEMS,
  ScopeNoteSource,
  ScopeTag,
  TPARuleTypes,
  TicSheetType,
  UPDATE_AFTER_HOURS,
  VerboseSowPdfMode,
  XACTIMATE_CATEGORIES,
  XCELERATE_STATUSES,
  getDefaultActivity,
  getEstimateTypeLabel,
  getNoteTypeFromEstimateType,
  getQueuedItemToProcessStatusName
} from "../chunk-J32YGLMO.mjs";
import {
  PROJECT_ROOM_ID
} from "../chunk-NAKG4HRS.mjs";
import {
  configureSharedAPI,
  getSharedAuthHeaders,
  getSharedBaseUrl
} from "../chunk-Y7KXQJM2.mjs";

// src/types/crmIntegrations.ts
var CRMIntegrationType = /* @__PURE__ */ ((CRMIntegrationType2) => {
  CRMIntegrationType2["JOB_NIMBUS"] = "JOB_NIMBUS";
  CRMIntegrationType2["XCELERATE"] = "XCELERATE";
  CRMIntegrationType2["FIRST_ATLANTIC"] = "FIRST_ATLANTIC";
  CRMIntegrationType2["ALBI"] = "ALBI";
  CRMIntegrationType2["PSA"] = "PSA";
  return CRMIntegrationType2;
})(CRMIntegrationType || {});
var CRM_ESTIMATE_CREATION_SOURCES = Object.values(EstimateCreationSource).filter(
  (source) => Object.values(CRMIntegrationType).includes(source)
);
var AlbiEstimateNameFormat = /* @__PURE__ */ ((AlbiEstimateNameFormat2) => {
  AlbiEstimateNameFormat2["CUSTOMER_NAME"] = "customerName";
  AlbiEstimateNameFormat2["ADDRESS2"] = "address2";
  return AlbiEstimateNameFormat2;
})(AlbiEstimateNameFormat || {});
var CRM_INTEGRATION_SLUG_PATTERN = /^[a-z0-9-]+$/;
var CRM_INTEGRATION_FIELDS = {
  ["JOB_NIMBUS" /* JOB_NIMBUS */]: {
    apiKey: { kind: "string", required: true },
    estimateTypesToSync: { kind: "estimateTypes", required: true }
  },
  ["XCELERATE" /* XCELERATE */]: {
    apiKey: { kind: "string", required: true },
    autoRegisterWebhook: { kind: "boolean", required: true },
    enableAssignee: { kind: "boolean", required: true },
    webhookUrl: { kind: "string", required: false }
  },
  ["FIRST_ATLANTIC" /* FIRST_ATLANTIC */]: {
    apiKey: { kind: "string", required: true },
    outboundApiKey: { kind: "string", required: false }
  },
  ["ALBI" /* ALBI */]: {
    id: { kind: "slug", required: true, unique: true },
    apiKey: { kind: "string", required: true },
    autoRegisterWebhook: { kind: "boolean", required: true },
    webhookUrl: { kind: "string", required: false },
    estimateNameFormat: { kind: "albiEstimateNameFormat", required: false }
  },
  ["PSA" /* PSA */]: {
    baseUrl: { kind: "url", required: true },
    schemaId: { kind: "string", required: true },
    apiKey: { kind: "string", required: true },
    syncType: { kind: "number", required: true },
    uploadFolderPath: { kind: "string", required: false },
    syncJobs: { kind: "boolean", required: false },
    syncStartDate: { kind: "isoDate", required: false, requiredWhen: "syncJobs" }
  }
};
var CRM_INTEGRATION_BASE_KEYS = ["type", "enabled"];

// src/types/feature-flags.ts
var FEATURE_FLAGS = [
  { key: "SHOW_DOCUSKETCH_UPLOAD", label: "DocuSketch Upload" },
  { key: "SCOPE_NOTE_UPLOAD_CONFIG", label: "Scope Note Upload Config" },
  { key: "MITIGATION_ENABLED", label: "Mitigation Enabled" },
  { key: "CONTENTS_ENABLED", label: "Contents Enabled" },
  { key: "MOLD_ENABLED", label: "Mold Enabled" },
  { key: "FIRE_MITIGATION_ENABLED", label: "Fire Mitigation Enabled" },
  { key: "BIOHAZARD_ENABLED", label: "Biohazard Enabled" },
  { key: "TIME_AND_MATERIAL_ENABLED", label: "Time and Material Enabled" },
  { key: "XCELERATE_ID_ENABLED", label: "Xcelerate ID Enabled" },
  { key: "MIT_TO_RECON", label: "Mit-to-Recon Conversion" },
  { key: "MOLD_TO_RECON", label: "Mold-to-Recon Conversion" },
  { key: "TIC_SHEETS", label: "Tic Sheets" },
  { key: "MOBILE_EXCEL_UPLOAD", label: "Mobile Excel Upload" },
  { key: "MOBILE_ADDRESS_UPLOAD", label: "Mobile Address Upload" },
  { key: "RECON_VOICE_AGENT_ENABLED", label: "Recon Voice Agent Enabled" },
  { key: "PDF_EXPORT_ENABLED", label: "PDF Export Enabled" },
  { key: "PDF_EXPORT_VERBOSE", label: "PDF Export Verbose Mode" },
  { key: "PDF_PREVIEW_MOBILE_ENABLED", label: "Mobile PDF Preview Before Export" },
  { key: "V2_UI", label: "V2 UI" },
  { key: "V2_MOBILE_APP_ESTIMATE_DETAILS_VIEW", label: "Mobile Estimate Details View" },
  { key: "V2_MOBILE_APP_ON_DEVICE_TRANSCRIPTION", label: "Mobile On-Device Transcription" },
  { key: "OFFLINE_MODE_ENABLED", label: "Offline Mode" },
  { key: "LINE_ITEM_FEEDBACK", label: "Line Item Feedback" },
  { key: "V2_ARCHIVE_PROJECT_MOBILE", label: "Mobile Archive Project" },
  { key: "RESPONSIVE_NOTE_ENABLED", label: "Responsive Note Enabled" },
  { key: "RESPONSIVE_NOTE_CAMERA_CAPTURE", label: "Responsive Note Camera Capture" },
  { key: "RESPONSIVE_NOTES_BLANK_NOTE_ENABLED", label: "Responsive Notes Blank Note Enabled" },
  { key: "REESE_RESPONSIVE_NOTE_ENABLED", label: "Reese Enabled in Responsive Note (blank + templated)" },
  { key: "HIDE_LEGACY_NOTE_BUTTON", label: "Hide Beta badge + old note button" },
  { key: "ALLOW_USER_TO_SAVE_INVALID_FORMULAS", label: "Allow User To Save Bad Formulas" },
  { key: "SELF_SERVE_ADMIN_ENABLED", label: "Self-Serve Admin (Manage Users)" },
  { key: "MANAGE_OFFICES_ENABLED", label: "Manage Offices (requires Self-Serve Admin)" },
  { key: "PHOTO_LABELS_ENABLED", label: "Manage Photo Labels (requires Self-Serve Admin)" },
  { key: "USE_Q_A_REESE_SUMMARY", label: "Use Q&A Reese Summary for Scope Note" },
  { key: "WEBAPP_ROOM_DIMENSIONS_ENABLED", label: "Webapp Room Dimensions" },
  { key: "KEEP_FORMULAS_EXPANDED", label: "Keep Formulas Expanded (don't combine multiplier terms)" },
  { key: "SPACES_ENABLED", label: "Spaces Enabled" },
  { key: "FLOOR_PLAN_SCAN_ENABLED", label: "Floor Plan Scan (RoomPlan)" },
  { key: "VIBE_ESTIMATING_ENABLED", label: "Vibe Estimating Agent" },
  { key: "DASHBOARD_DEFAULT_ASSIGNEE_TO_SELF", label: "Default dashboard assignee filter to signed-in user" }
];

// src/types/photo-labels.ts
var PHOTO_LABEL_NAME_MAX_LENGTH = 100;
var MAX_PHOTO_LABELS_PER_ORG = 500;
var MAX_PHOTOS_PER_LABEL_APPLY = 500;
var FREE_FORM_PHOTO_LABEL_ID_PREFIX = "freeform:";
function photoLabelFreeFormId(name) {
  return `${FREE_FORM_PHOTO_LABEL_ID_PREFIX}${name.trim().slice(0, PHOTO_LABEL_NAME_MAX_LENGTH).toLowerCase()}`;
}
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function normalizePhotoLabels(raw) {
  if (!Array.isArray(raw)) return [];
  const seenNames = /* @__PURE__ */ new Set();
  const seenIds = /* @__PURE__ */ new Set();
  const labels = [];
  for (const entry of raw) {
    if (!isRecord(entry)) continue;
    if (typeof entry.id !== "string" || typeof entry.name !== "string") continue;
    const id = entry.id.trim();
    const name = entry.name.trim().slice(0, PHOTO_LABEL_NAME_MAX_LENGTH);
    if (!id || !name) continue;
    const nameKey = name.toLowerCase();
    if (seenIds.has(id) || seenNames.has(nameKey)) continue;
    seenIds.add(id);
    seenNames.add(nameKey);
    labels.push({ id, name });
    if (labels.length >= MAX_PHOTO_LABELS_PER_ORG) break;
  }
  return labels;
}
function formatPhotoLabelNames(raw) {
  return normalizePhotoLabels(raw).map((label) => label.name).join(", ");
}

// src/types/photo-report.ts
var PHOTO_REPORT_CAPTION_MAX_LENGTH = 300;

// src/types/responsive-note-photos.ts
var MAX_PHOTOS_PER_DELETE = 500;

// src/types/formula.ts
var FormatRawFormulaError = /* @__PURE__ */ ((FormatRawFormulaError2) => {
  FormatRawFormulaError2["UNEXPECTED_SYMBOLS"] = "Unexpected Symbols";
  FormatRawFormulaError2["FAILED_TO_PARSE"] = "Failed To Parse";
  FormatRawFormulaError2["CANNOT_MULTIPLY_DIFFERENT_UNITS"] = "Cannot Multiply Different Units";
  FormatRawFormulaError2["MISMATCHED_UNITS"] = "Mismatched Units";
  FormatRawFormulaError2["UNRECOGNIZED_UNITS"] = "Unrecognized Units";
  FormatRawFormulaError2["INCORRECT_UNITS"] = "Incorrect Units";
  FormatRawFormulaError2["ENCOUNTERED_ERROR_WHEN_PARSING"] = "Encountered Error When Parsing";
  FormatRawFormulaError2["IS_NEGATIVE"] = "Is Negative";
  FormatRawFormulaError2["MISSING_UNITS"] = "Missing Units";
  FormatRawFormulaError2["INVALID_FEET_INCH_NOTATION"] = "Invalid Feet/Inch Notation";
  FormatRawFormulaError2["TOO_MANY_PARENTHESIS"] = "Too Many Parentheses";
  FormatRawFormulaError2["FORMULA_TOO_BIG"] = "Formula Is Too Big";
  return FormatRawFormulaError2;
})(FormatRawFormulaError || {});

// src/types/module.ts
var FINAL_MODULE_TEMPLATE_ID = "final";
var ModuleLevel = /* @__PURE__ */ ((ModuleLevel2) => {
  ModuleLevel2["PROJECT"] = "project";
  ModuleLevel2["ROOM"] = "room";
  return ModuleLevel2;
})(ModuleLevel || {});
var ModuleType = /* @__PURE__ */ ((ModuleType2) => {
  ModuleType2["INITIAL"] = "INITIAL";
  ModuleType2["SUBSEQUENT"] = "SUBSEQUENT";
  return ModuleType2;
})(ModuleType || {});

// src/types/pricing.ts
var CustomPricing = /* @__PURE__ */ ((CustomPricing2) => {
  CustomPricing2["XACTIMATE"] = "xactimate";
  CustomPricing2["CUSTOM"] = "custom";
  return CustomPricing2;
})(CustomPricing || {});

// src/types/line-item.ts
var LineItemDiffPath = /* @__PURE__ */ ((LineItemDiffPath2) => {
  LineItemDiffPath2["Description"] = "lineItem.lineItem";
  LineItemDiffPath2["LineItemId"] = "lineItem.lineItemId";
  LineItemDiffPath2["Confirmed"] = "lineItem.confirmed";
  LineItemDiffPath2["AddedByTPARule"] = "lineItem.addedByTPARule";
  LineItemDiffPath2["RemovedByTPARule"] = "lineItem.removedByTPARule";
  LineItemDiffPath2["Comment"] = "lineItem.comment";
  LineItemDiffPath2["Formula"] = "lineItem.formula.rawFormula";
  LineItemDiffPath2["RoomName"] = "tuple.pageMetadata.roomName";
  LineItemDiffPath2["IssueExplanation"] = "lineItem.issueExplanation";
  LineItemDiffPath2["HasError"] = "tuple.hasError";
  LineItemDiffPath2["Confidence"] = "lineItem.confidence";
  LineItemDiffPath2["Archived"] = "lineItem.archived";
  LineItemDiffPath2["IsLikely"] = "tuple.isLikely";
  LineItemDiffPath2["IsComplement"] = "lineItem.isComplement";
  LineItemDiffPath2["IsMutuallyExcluded"] = "lineItem.isMutuallyExcluded";
  LineItemDiffPath2["LineItemCreated"] = "";
  return LineItemDiffPath2;
})(LineItemDiffPath || {});

// src/types/mit-to-recon-mapping.ts
var MitToReconMappingAction = /* @__PURE__ */ ((MitToReconMappingAction2) => {
  MitToReconMappingAction2["DROP"] = "DROP";
  MitToReconMappingAction2["KEEP_AS_SAME"] = "KEEP_AS_SAME";
  MitToReconMappingAction2["USE_HARDCODED_MAPPING"] = "USE_HARDCODED_MAPPING";
  MitToReconMappingAction2["SEND_TO_LLM"] = "SEND_TO_LLM";
  return MitToReconMappingAction2;
})(MitToReconMappingAction || {});

// src/types/manual-estimate-review.ts
var ManualEstimateReviewStage = /* @__PURE__ */ ((ManualEstimateReviewStage2) => {
  ManualEstimateReviewStage2["NotStarted"] = "Not Started";
  ManualEstimateReviewStage2["InProgress"] = "In Progress";
  ManualEstimateReviewStage2["InReview"] = "In Review";
  ManualEstimateReviewStage2["Approved"] = "Approved";
  ManualEstimateReviewStage2["Active"] = "Active";
  ManualEstimateReviewStage2["Archived"] = "Archived";
  return ManualEstimateReviewStage2;
})(ManualEstimateReviewStage || {});
var ManualEstimateReviewUploadSource = /* @__PURE__ */ ((ManualEstimateReviewUploadSource2) => {
  ManualEstimateReviewUploadSource2["Script"] = "script";
  ManualEstimateReviewUploadSource2["ToolsUi"] = "tools_ui";
  return ManualEstimateReviewUploadSource2;
})(ManualEstimateReviewUploadSource || {});

// src/types/deploy.ts
var DEPLOY_SERVICES = ["webapp", "api", "tools"];
var VERCEL_DEPLOY_SERVICES = ["webapp", "tools"];
var DEPLOY_ENVIRONMENTS = ["staging", "production"];
var DEPLOY_PROVIDERS = ["vercel", "railway"];

// src/types/job.ts
var JobStatus = /* @__PURE__ */ ((JobStatus2) => {
  JobStatus2["QUEUED"] = "queued";
  JobStatus2["PROCESSING"] = "processing";
  JobStatus2["COMPLETED"] = "completed";
  JobStatus2["FAILED"] = "failed";
  return JobStatus2;
})(JobStatus || {});
var JobType = /* @__PURE__ */ ((JobType2) => {
  JobType2["GENERATE_ESTIMATE"] = "generate_estimate";
  JobType2["CAT_SYNC"] = "cat_sync";
  JobType2["APPLY_TPA_RULES"] = "apply_tpa_rules";
  JobType2["CREATE_MANUAL_ESTIMATE_REVIEW"] = "create_manual_estimate_review";
  return JobType2;
})(JobType || {});
var isNewFoundationSource = (value) => "source" in value && value.source === "new_foundation";
var isNewFoundationScopeNoteJob = (job) => isNewFoundationSource(job);
var isLegacyEstimateJob = (job) => !isNewFoundationScopeNoteJob(job);

// src/utils/formula-utils/formulaUnits.ts
var FormulaUnit = /* @__PURE__ */ ((FormulaUnit2) => {
  FormulaUnit2["SF"] = "SF";
  FormulaUnit2["LF"] = "LF";
  FormulaUnit2["EA"] = "EA";
  FormulaUnit2["RL"] = "RL";
  FormulaUnit2["HR"] = "HR";
  FormulaUnit2["SQ"] = "SQ";
  FormulaUnit2["DA"] = "DA";
  FormulaUnit2["CF"] = "CF";
  FormulaUnit2["GL"] = "GL";
  FormulaUnit2["WK"] = "WK";
  FormulaUnit2["MO"] = "MO";
  FormulaUnit2["BF"] = "BF";
  FormulaUnit2["LB"] = "LB";
  FormulaUnit2["FiveGallon"] = "5G";
  FormulaUnit2["CY"] = "CY";
  FormulaUnit2["TN"] = "TN";
  FormulaUnit2["SY"] = "SY";
  FormulaUnit2["PL"] = "PL";
  return FormulaUnit2;
})(FormulaUnit || {});
var FORMULA_UNIT_VALUES = Object.values(FormulaUnit);

// src/utils/formula-utils/formulaShorthands.ts
var RoomMeasurementKey = /* @__PURE__ */ ((RoomMeasurementKey2) => {
  RoomMeasurementKey2["Walls"] = "w";
  RoomMeasurementKey2["Floors"] = "f";
  RoomMeasurementKey2["Ceilings"] = "c";
  RoomMeasurementKey2["FloorPerimeter"] = "pf";
  RoomMeasurementKey2["CeilingPerimeter"] = "pc";
  return RoomMeasurementKey2;
})(RoomMeasurementKey || {});
var FormulaShorthand = /* @__PURE__ */ ((FormulaShorthand4) => {
  FormulaShorthand4["WC"] = "WC";
  FormulaShorthand4["W"] = "W";
  FormulaShorthand4["C"] = "C";
  FormulaShorthand4["F"] = "F";
  FormulaShorthand4["FLR_RPL"] = "FLR_RPL";
  FormulaShorthand4["FLR_RMV"] = "FLR_RMV";
  FormulaShorthand4["PF"] = "PF";
  FormulaShorthand4["PC"] = "PC";
  FormulaShorthand4["PFBS"] = "PFBS";
  FormulaShorthand4["HH"] = "HH";
  FormulaShorthand4["LL"] = "LL";
  FormulaShorthand4["SL"] = "SL";
  FormulaShorthand4["V"] = "V";
  FormulaShorthand4["FBS"] = "FBS";
  FormulaShorthand4["WBS"] = "WBS";
  FormulaShorthand4["P"] = "P";
  FormulaShorthand4["R"] = "R";
  FormulaShorthand4["HIP"] = "HIP";
  FormulaShorthand4["VAL"] = "VAL";
  FormulaShorthand4["EAVE"] = "EAVE";
  FormulaShorthand4["EW"] = "EW";
  FormulaShorthand4["WOP"] = "WOP";
  FormulaShorthand4["SQ"] = "SQ";
  FormulaShorthand4["PR"] = "PR";
  return FormulaShorthand4;
})(FormulaShorthand || {});
var AREA_SHORTHANDS = ["W" /* W */, "F" /* F */, "C" /* C */, "WC" /* WC */];
var PERIMETER_SHORTHANDS = ["PF" /* PF */, "PC" /* PC */];
var FORMULA_SHORTHAND_VALUES = Object.values(FormulaShorthand);
var containsCappableShorthand = (rawFormula) => {
  const tokens = rawFormula.toUpperCase().match(/[A-Z_]+/g) ?? [];
  return tokens.some((token) => FORMULA_SHORTHAND_VALUES.includes(token));
};
var compareShorthands = (unitA, unitB) => {
  const aIndex = FORMULA_SHORTHAND_VALUES.indexOf(unitA);
  const bIndex = FORMULA_SHORTHAND_VALUES.indexOf(unitB);
  const aIsShorthand = aIndex !== -1;
  const bIsShorthand = bIndex !== -1;
  if (!aIsShorthand && !bIsShorthand) return 0;
  if (aIsShorthand && !bIsShorthand) return -1;
  if (!aIsShorthand && bIsShorthand) return 1;
  return aIndex - bIndex;
};
var SHORTHAND_TO_UNIT = {
  // Room-based area
  ["W" /* W */]: ["SF" /* SF */],
  ["C" /* C */]: ["SF" /* SF */],
  ["F" /* F */]: ["SF" /* SF */],
  ["WC" /* WC */]: ["SF" /* SF */],
  ["FLR_RPL" /* FLR_RPL */]: ["SF" /* SF */],
  ["FLR_RMV" /* FLR_RMV */]: ["SF" /* SF */],
  // Room-based perimeter (PF/PC can pair with both LF and SF in allowedPairs)
  ["PF" /* PF */]: ["LF" /* LF */, "SF" /* SF */],
  ["PC" /* PC */]: ["LF" /* LF */, "SF" /* SF */],
  ["PFBS" /* PFBS */]: ["LF" /* LF */],
  // Room-based dimensional
  ["HH" /* HH */]: ["LF" /* LF */],
  ["LL" /* LL */]: ["LF" /* LF */],
  ["SL" /* SL */]: ["LF" /* LF */],
  ["V" /* V */]: ["CF" /* CF */],
  ["FBS" /* FBS */]: ["SF" /* SF */],
  ["WBS" /* WBS */]: ["SF" /* SF */],
  // Roofing
  ["P" /* P */]: ["LF" /* LF */],
  ["R" /* R */]: ["LF" /* LF */],
  ["HIP" /* HIP */]: ["LF" /* LF */],
  ["VAL" /* VAL */]: ["LF" /* LF */],
  ["SQ" /* SQ */]: ["SQ" /* SQ */],
  ["EAVE" /* EAVE */]: ["LF" /* LF */, "SF" /* SF */],
  // Exterior (non-roofing)
  ["EW" /* EW */]: ["SF" /* SF */],
  ["WOP" /* WOP */]: ["LF" /* LF */],
  // Other
  ["PR" /* PR */]: ["LF" /* LF */]
};
var SHORTHAND_DESCRIPTION_FOR_LLM = {
  // Room-based area
  ["W" /* W */]: "the square footage of the Walls (square feet)",
  ["C" /* C */]: "the square footage of the Ceiling (square feet)",
  ["F" /* F */]: "the square footage of the Floor (square feet)",
  ["WC" /* WC */]: "the square footage of the Walls and Ceiling (square feet)",
  // Room-based perimeter
  ["PF" /* PF */]: "the perimeter of the Floor (linear feet)",
  ["PC" /* PC */]: "the perimeter of the Ceiling (linear feet)"
};

// src/utils/formula-utils/utils.ts
var FIRST_QUANTITY_IN_STRING_REGEX = /(\-?[0-9]*\.?[0-9]+)/;
var FIRST_UNIT_IN_STRING_REGEX = /([a-zA-Z]+)/;
var FORMULA_COMPONENT_REGEX = /(?:\+|\-)?\s*[\.0-9]*\s*[a-zA-Z]+/gi;
var FLOOD_CUT_LINE_ITEM_IDS = [
  "5e235b16-4e5e-4db9-97f1-9bbb5634e463",
  // walls__drywall_flood_cut_lf__replace
  "bad944ec-e9bb-4c32-84ac-e94b8061d7c5"
  // tear_out_wet_drywall_per_linear_foot_flood_cut
];
var normalizeString = (str) => str.toLocaleLowerCase().trim().replace(/\s+/g, " ").trim();
var preciseRound = (num, decimalPlaces) => {
  return Math.round((num + Number.EPSILON) * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
};
var extractUnitFromFormulaComponent = (formulaComponent) => formulaComponent.replaceAll(/[^a-zA-Z_]+/g, "").trim().toUpperCase();
var formatFormulaComponent = (quantity, unit) => {
  if (Object.values(FormulaShorthand).includes(unit.trim().toUpperCase()) && quantity === 1) {
    return unit.toUpperCase().trim();
  }
  return `${preciseRound(quantity, 2)} ${unit.toUpperCase()}`.replace(/^0\./, ".");
};

// src/utils/formula-utils/breakUpFormulaIntoComponents.ts
import { parse } from "mathjs";
var _insertMultiplicationBetweenNumberAndUnit = (formula) => formula.replace(/(\d+\.?\d*|\.\d+)\s*([A-Za-z]+)/g, "($1*$2)");
var _removeMultiplicationBetweenNumberAndUnit = (str) => str.replace(/\(\s*/g, "").replace(/\s*\)/g, "").replaceAll(" * ", " ");
var _collectAdditiveTerms = (node, negate) => {
  if (node.type === "OperatorNode") {
    const op = node.op;
    const args = node.args;
    if (op === "+") {
      return [..._collectAdditiveTerms(args[0], negate), ..._collectAdditiveTerms(args[1], negate)];
    }
    if (op === "-" && args.length === 2) {
      return [..._collectAdditiveTerms(args[0], negate), ..._collectAdditiveTerms(args[1], !negate)];
    }
    if (op === "-" && args.length === 1) {
      return _collectAdditiveTerms(args[0], !negate);
    }
  }
  let str = _removeMultiplicationBetweenNumberAndUnit(node.toString());
  if (negate) {
    str = str.startsWith("-") ? str.slice(1) : `-${str}`;
  }
  return [str];
};
var breakUpFormulaIntoComponents = (formulaRaw) => {
  try {
    const preprocessed = _insertMultiplicationBetweenNumberAndUnit(formulaRaw.trim().toUpperCase());
    const parsed = parse(preprocessed);
    return _collectAdditiveTerms(parsed, false).filter((comp) => !!comp);
  } catch {
    return formulaRaw.trim().replaceAll("-", "+-").trim().split(/(?:\+|\(|\))/).filter((comp) => !!comp);
  }
};

// src/utils/formula-utils/validateUserTypedFormula.ts
import { ConstantNode, OperatorNode, parse as parse2, simplify } from "mathjs";
var validateUserTypedFormula = (rawFormula, correctUnit, opts) => {
  try {
    const { convertedFormula, errorMessage: feetInchError } = _convertFeetInchNotationToLf(rawFormula);
    if (feetInchError) return { errorMessage: feetInchError };
    const normalizedFormula = _normalizeMultiplicationOperator(convertedFormula);
    const unexpectedSymbolsError = _rejectUnexpectedSymbols(normalizedFormula);
    if (unexpectedSymbolsError) return { errorMessage: unexpectedSymbolsError };
    const { strippedFormula, trailingUnit } = _stripRedundantTrailingUnit(normalizedFormula);
    const effectiveCorrectUnit = correctUnit ?? trailingUnit;
    const { parsedFormula, errorMessage: parseError } = _parseAndSimplifyFormula(strippedFormula);
    if (parseError || parsedFormula === void 0) return { errorMessage: parseError ?? "Failed To Parse" /* FAILED_TO_PARSE */ };
    const { distributedFormula, errorMessage: distributeError } = _distributeMultipliersAndRejectParens(parsedFormula);
    if (distributeError || distributedFormula === void 0)
      return { errorMessage: distributeError ?? "Failed To Parse" /* FAILED_TO_PARSE */ };
    const collapsedFormula = _collapseLfMultiplications(distributedFormula);
    const multipliedUnitsError = _rejectMultipliedUnits(collapsedFormula);
    if (multipliedUnitsError) return { errorMessage: multipliedUnitsError };
    const { extractedUnits, errorMessage } = _extractUnitFromFormula(collapsedFormula);
    if (errorMessage) return { errorMessage };
    if (effectiveCorrectUnit === void 0 && !extractedUnits?.length) {
      if (!opts?.allowNoUnits) return { errorMessage: "Missing Units" /* MISSING_UNITS */ };
      const noUnitNegativeError = _rejectNegativeResult(collapsedFormula);
      if (noUnitNegativeError) return { errorMessage: noUnitNegativeError };
      return { rawFormulaFormatted: collapsedFormula };
    }
    if (effectiveCorrectUnit !== void 0 && !!extractedUnits?.length && !extractedUnits.some((extractedUnit) => extractedUnit === effectiveCorrectUnit))
      return { errorMessage: "Incorrect Units" /* INCORRECT_UNITS */ };
    const effectiveUnit = effectiveCorrectUnit ?? extractedUnits[0];
    const unitToQuantityMap = _buildUnitToQuantityMap(collapsedFormula, effectiveUnit);
    if (opts?.enforceMaxQuantityConstraint) _enforceMaxQuantityConstraint(unitToQuantityMap);
    _recombineWAndCIntoWC(unitToQuantityMap);
    const rawFormulaFormatted = _formatSortedComponents(unitToQuantityMap, effectiveUnit, opts?.keepUnitInRawFormula);
    const negativeError = _rejectNegativeResult(rawFormulaFormatted);
    if (negativeError) return { errorMessage: negativeError };
    return { rawFormulaFormatted };
  } catch (e) {
    console.log(e);
    return { errorMessage: "Encountered Error When Parsing" /* ENCOUNTERED_ERROR_WHEN_PARSING */ };
  }
};
var abbrevValues = Object.values(FormulaShorthand);
var _stripRedundantTrailingUnit = (formula) => {
  const trimmed = formula.trim();
  const match = trimmed.match(/^(.+?)\s+([A-Za-z]+)\s*$/);
  if (!match) return { strippedFormula: formula };
  const [, prefix, trailingRaw] = match;
  const trailing = trailingRaw.toUpperCase();
  if (!FORMULA_UNIT_VALUES.includes(trailing)) return { strippedFormula: formula };
  const prefixUpper = prefix.toUpperCase();
  for (const shorthand of FORMULA_SHORTHAND_VALUES) {
    const shorthandPresent = new RegExp(`(?<![A-Z_])${shorthand}(?![A-Z_])`).test(prefixUpper);
    if (!shorthandPresent) continue;
    if (SHORTHAND_TO_UNIT[shorthand].includes(trailing)) {
      return { strippedFormula: prefix, trailingUnit: trailing };
    }
  }
  return { strippedFormula: formula };
};
var _convertFeetInchNotationToLf = (formula) => {
  let invalidInches = false;
  let result = formula.replace(/(\d+(?:\.\d+)?)\s*'\s*\.?\s*(\d+(?:\.\d+)?)\s*(?:''|")(?:\s+LF(?![A-Z_]))?/gi, (_match, feet, inches) => {
    const inchesNum = Number(inches);
    if (inchesNum >= 12) {
      invalidInches = true;
      return _match;
    }
    const value = Number(feet) + inchesNum / 12;
    return `${preciseRound(value, 4)} LF`;
  });
  result = result.replace(/(\d+(?:\.\d+)?)\s*(?:''|")(?:\s+LF(?![A-Z_]))?/gi, (_match, inches) => {
    const value = Number(inches) / 12;
    return `${preciseRound(value, 4)} LF`;
  });
  result = result.replace(/(\d+(?:\.\d+)?)\s*'(?:\s+LF(?![A-Z_]))?/gi, (_match, feet) => {
    return `${Number(feet)} LF`;
  });
  if (invalidInches) return { convertedFormula: result, errorMessage: "Invalid Feet/Inch Notation" /* INVALID_FEET_INCH_NOTATION */ };
  return { convertedFormula: result };
};
var _normalizeMultiplicationOperator = (formula) => {
  return formula.replace(/\bx\b/gi, "*");
};
var _rejectUnexpectedSymbols = (formula) => {
  if (/[^a-zA-Z0-9\s+\-*/.()_]/.test(formula)) {
    return "Unexpected Symbols" /* UNEXPECTED_SYMBOLS */;
  }
  return null;
};
var MAX_FORMULA_NODE_DEPTH = 14;
var _nodeDepth = (node) => {
  if (node.type === "ParenthesisNode") return _nodeDepth(node.content);
  let maxChildDepth = 0;
  node.forEach((child) => {
    maxChildDepth = Math.max(maxChildDepth, _nodeDepth(child));
  });
  return 1 + maxChildDepth;
};
var _parseAndSimplifyFormula = (formula) => {
  try {
    const withExplicitMultiplication = formula.toUpperCase().replace(/(\d+\.?\d*|\.\d+)\s*([A-Za-z_]+)/g, "($1*$2)");
    const parsed = parse2(withExplicitMultiplication);
    if (_nodeDepth(parsed) > MAX_FORMULA_NODE_DEPTH) return { errorMessage: "Formula Is Too Big" /* FORMULA_TOO_BIG */ };
    return { parsedFormula: _formatNode(parsed) };
  } catch (e) {
    console.error(`Encountered error when attempting to parse raw formula: `, e);
    return { errorMessage: "Failed To Parse" /* FAILED_TO_PARSE */ };
  }
};
var _unwrapParen = (node) => node.type === "ParenthesisNode" ? _unwrapParen(node.content) : node;
var _isAdditive = (node) => node.type === "OperatorNode" && (node.op === "+" || node.op === "-") && node.args.length === 2;
var _distributeProduct = (multiplier, target) => {
  if (!_isAdditive(target)) return null;
  const [t1, t2] = target.args;
  const m1 = new OperatorNode("*", "multiply", [multiplier, t1]);
  const m2 = new OperatorNode("*", "multiply", [multiplier, t2]);
  return new OperatorNode(target.op, target.fn, [m1, m2]);
};
var _distributeNode = (node) => {
  let n = node.map((child) => _distributeNode(child));
  if (n.type === "OperatorNode" && n.op === "*" && n.args.length === 2) {
    const a = _unwrapParen(n.args[0]);
    const b = _unwrapParen(n.args[1]);
    const distributed = _distributeProduct(a, b) ?? _distributeProduct(b, a);
    if (distributed) return _distributeNode(distributed);
    if (a.type === "ConstantNode" && b.type === "ConstantNode") return new ConstantNode(a.value * b.value);
    n = new OperatorNode("*", "multiply", [a, b]);
  }
  return n;
};
var _distributeMultipliersAndRejectParens = (formula) => {
  try {
    const distributed = _formatDistributedNode(_distributeNode(parse2(formula)));
    if (distributed.includes("(")) return { errorMessage: "Too Many Parentheses" /* TOO_MANY_PARENTHESIS */ };
    return { distributedFormula: distributed };
  } catch (e) {
    console.error(`Encountered error when distributing multipliers: `, e);
    return { errorMessage: "Failed To Parse" /* FAILED_TO_PARSE */ };
  }
};
var _formatDistributedNode = (node) => {
  if (node.type === "OperatorNode") {
    const op = node.op;
    const args = node.args;
    if (op === "+") return `${_formatDistributedNode(args[0])} + ${_formatDistributedNode(args[1])}`;
    if (op === "-" && args.length === 2) return `${_formatDistributedNode(args[0])} - ${_formatDistributedNode(args[1])}`;
    if (op === "-" && args.length === 1) return `-${_formatDistributedNode(args[0])}`;
  }
  return _formatNode(node);
};
var _collapseLfMultiplications = (formula) => {
  const lfComponentSrc = String.raw`(\d+(?:\.\d+)?)\s+LF`;
  const multiSeparationSrc = String.raw`\s*\*\s*`;
  const squareLfRegex = new RegExp(`${lfComponentSrc}${multiSeparationSrc}${lfComponentSrc}\\b`, "g");
  const cubeLfRegex = new RegExp(`${lfComponentSrc}${multiSeparationSrc}${lfComponentSrc}${multiSeparationSrc}${lfComponentSrc}\\b`, "g");
  let result = formula;
  result = result.replace(/LF\s*\^\s*3/g, "CF");
  result = result.replace(/LF\s*\^\s*2/g, "SF");
  result = result.replace(cubeLfRegex, (_match, a, b, c) => `${preciseRound(Number(a) * Number(b) * Number(c), 4)} CF`);
  result = result.replace(squareLfRegex, (_match, a, b) => `${preciseRound(Number(a) * Number(b), 4)} SF`);
  return result;
};
var _recombineWAndCIntoWC = (unitToQuantityMap) => {
  const wKey = "W" /* W */;
  const cKey = "C" /* C */;
  const wcKey = "WC" /* WC */;
  const wQty = unitToQuantityMap[wKey];
  const cQty = unitToQuantityMap[cKey];
  if (wQty !== void 0 && cQty !== void 0 && wQty === cQty && wQty !== 0) {
    delete unitToQuantityMap[wKey];
    delete unitToQuantityMap[cKey];
    unitToQuantityMap[wcKey] = wQty;
  }
};
var _rejectMultipliedUnits = (formula) => {
  if (formula.includes(" * ")) {
    return "Cannot Multiply Different Units" /* CANNOT_MULTIPLY_DIFFERENT_UNITS */;
  }
  return null;
};
var _extractUnitFromFormula = (formula) => {
  const formulaComponents = breakUpFormulaIntoComponents(formula);
  if (!formulaComponents?.length) return { errorMessage: "Failed To Parse" /* FAILED_TO_PARSE */ };
  let extractedUnits;
  for (const component of formulaComponents) {
    const unit = extractUnitFromFormulaComponent(component);
    if (!unit) {
      continue;
    }
    let allowed;
    if (unit in SHORTHAND_TO_UNIT) {
      allowed = SHORTHAND_TO_UNIT[unit];
    } else if (Object.values(FormulaUnit).includes(unit)) {
      allowed = [unit];
    } else {
      return { errorMessage: "Unrecognized Units" /* UNRECOGNIZED_UNITS */ };
    }
    extractedUnits = extractedUnits === void 0 ? allowed : extractedUnits.filter((u) => allowed.includes(u));
    if (extractedUnits.length === 0) return { errorMessage: "Mismatched Units" /* MISMATCHED_UNITS */ };
  }
  return { extractedUnits };
};
var _buildUnitToQuantityMap = (rawFormula, effectiveUnit) => {
  const formulaComponents = breakUpFormulaIntoComponents(rawFormula);
  const unitToQuantityMap = {};
  for (const component of formulaComponents) {
    const unit = extractUnitFromFormulaComponent(component) || effectiveUnit;
    const quantity = _extractQuantityFromFormulaComponent(component);
    if (unit === "WC" /* WC */) {
      const wKey = "W" /* W */;
      const cKey = "C" /* C */;
      unitToQuantityMap[wKey] ??= 0;
      unitToQuantityMap[wKey] += quantity;
      unitToQuantityMap[cKey] ??= 0;
      unitToQuantityMap[cKey] += quantity;
      continue;
    }
    unitToQuantityMap[unit] ??= 0;
    unitToQuantityMap[unit] += quantity;
  }
  return unitToQuantityMap;
};
var _enforceMaxQuantityConstraint = (unitToQuantityMap) => {
  for (const shorthand of AREA_SHORTHANDS) {
    const key = shorthand;
    const qty = unitToQuantityMap[key];
    if (qty !== void 0 && qty > 1) {
      unitToQuantityMap[key] = 1;
    }
  }
};
var _formatSortedComponents = (unitToQuantityMap, correctUnit, keepUnitInRawFormula) => {
  const simplifiedFormulaComponents = Object.entries(unitToQuantityMap).map(
    ([unit, quantity]) => unit === correctUnit && !keepUnitInRawFormula ? `${preciseRound(quantity, 2)}` : `${preciseRound(quantity, 2)} ${unit}`
  );
  simplifiedFormulaComponents.sort((a, b) => compareShorthands(extractUnitFromFormulaComponent(a), extractUnitFromFormulaComponent(b)));
  let result = "";
  for (const component of simplifiedFormulaComponents) {
    const unit = extractUnitFromFormulaComponent(component);
    const quantity = _extractQuantityFromFormulaComponent(component);
    const isAbbreviation = abbrevValues.includes(unit);
    let formatted = component.trim();
    if (isAbbreviation && quantity === 1) {
      formatted = unit;
    }
    formatted = formatted.replace(/\b0\./, ".");
    if (result === "") {
      result = formatted;
    } else if (formatted.startsWith("-")) {
      result = `${result} - ${formatted.slice(1).trim()}`;
    } else {
      result = `${result} + ${formatted}`;
    }
  }
  return result;
};
var _rejectNegativeResult = (formula) => {
  if (formula.trim().startsWith("-")) {
    return "Is Negative" /* IS_NEGATIVE */;
  }
};
var _containsSymbol = (node) => {
  if (node.type === "SymbolNode") return true;
  let found = false;
  node.forEach((child) => {
    if (_containsSymbol(child)) found = true;
  });
  return found;
};
var _simplifyRules = [...simplify.rules, "v1 * c1 -> c1 * v1"];
var _simplifyOptions = { exactFractions: false };
var _formatNode = (node) => {
  if (node.type === "OperatorNode" && node.op === "*") {
    const args = node.args;
    if (args.length === 2 && _containsSymbol(args[0]) && _containsSymbol(args[1])) {
      return `${_formatNode(args[0])} * ${_formatNode(args[1])}`;
    }
  }
  const simplified = simplify(node, _simplifyRules, {}, _simplifyOptions);
  return simplified.toString().replaceAll(" * ", " ").toUpperCase();
};
var _extractQuantityFromFormulaComponent = (formulaComponent) => {
  const match = formulaComponent.match(FIRST_QUANTITY_IN_STRING_REGEX);
  if (!match && formulaComponent.startsWith("-")) return -1;
  if (!match) return 1;
  return Number(match[1]);
};

// src/utils/formula-utils/extractUnitFromFormula.ts
var extractUnitFromFormula = (formulaRaw) => {
  const formattedFormula = validateUserTypedFormula(formulaRaw, void 0, { keepUnitInRawFormula: true }).rawFormulaFormatted ?? formulaRaw;
  const formulaComponents = breakUpFormulaIntoComponents(formattedFormula);
  let formulaUnit;
  for (const component of formulaComponents) {
    let componentUnit = extractUnitFromFormulaComponent(component).trim().toUpperCase();
    if (componentUnit in SHORTHAND_TO_UNIT) {
      componentUnit = SHORTHAND_TO_UNIT[componentUnit][0].trim().toUpperCase();
    }
    if (formulaUnit === void 0) {
      formulaUnit = componentUnit;
    } else if (formulaUnit !== componentUnit) {
      return void 0;
    }
  }
  if (formulaUnit === "") {
    return void 0;
  }
  return formulaUnit;
};

// src/utils/formula-utils/checkIfFormulaHasCorrectUnits.ts
var checkIfFormulaHasCorrectUnits = (rawFormula, correctUnit) => {
  const formulaComponents = breakUpFormulaIntoComponents(rawFormula);
  let prevUnit = "";
  for (const component of formulaComponents) {
    const unitInComponent = extractUnitFromFormulaComponent(component);
    if (!unitInComponent) {
      continue;
    }
    if (prevUnit !== void 0 && unitInComponent === prevUnit) {
      return false;
    }
    prevUnit = unitInComponent;
    const isUnitValid = unitInComponent in SHORTHAND_TO_UNIT ? SHORTHAND_TO_UNIT[unitInComponent].some((allowedUnit) => allowedUnit === correctUnit) : unitInComponent === correctUnit;
    if (isUnitValid === false) {
      return false;
    }
  }
  return true;
};

// src/utils/formula-utils/convertFormulaToDifferentUnit.ts
var NUM_SF_PER_SQ = 100;
var NUM_LF_PER_RL = 1e3;
var convertFormulaToDifferentUnit = (parentLineItemId, parentLineItemStr, rawFormula, correctUnit, opts) => {
  const combine = opts?.combine ?? true;
  const _doesRawFormulaMatchUnit = (formulaRaw, correctUnit2) => !!normalizeString(formulaRaw).match(new RegExp(`^(?:\\-?[0-9]*\\.?[0-9]+)?\\s*${correctUnit2}$`, "i"));
  const _replaceUnitInRawFormula = (formulaRaw, oldUnit, newUnit) => formulaRaw.toUpperCase().replace(oldUnit.toUpperCase(), newUnit.toUpperCase());
  const allowLinearFeetToSquareFeet = !!opts?.allowLinearFeetToSquareFeet;
  const forceUnitConversion = !!opts?.forceUnitConversion;
  const parentQuantityStr = rawFormula.match(FIRST_QUANTITY_IN_STRING_REGEX)?.[1];
  const parentQuantity = parentQuantityStr ? Number(parentQuantityStr) : void 0;
  const isUnitLf = _doesRawFormulaMatchUnit(rawFormula, "lf");
  const isUnitPf = _doesRawFormulaMatchUnit(rawFormula, "pf");
  const isUnitPc = _doesRawFormulaMatchUnit(rawFormula, "pc");
  if (_doesRawFormulaMatchUnit(rawFormula, "ea") && correctUnit === "DA") return `${parentQuantity} DA`;
  if (_doesRawFormulaMatchUnit(rawFormula, "ea") && correctUnit === "HR") return `${parentQuantity} HR`;
  if (_doesRawFormulaMatchUnit(rawFormula, "ea") && correctUnit === "RL") return `${parentQuantity} RL`;
  if (_doesRawFormulaMatchUnit(rawFormula, "ea") && correctUnit === "GL") return `${parentQuantity} GL`;
  if (_doesRawFormulaMatchUnit(rawFormula, "ea") && correctUnit === "WK") return `${parentQuantity} WK`;
  if (_doesRawFormulaMatchUnit(rawFormula, "hr") && correctUnit === "EA") return `${parentQuantity} EA`;
  if (_doesRawFormulaMatchUnit(rawFormula, "da") && correctUnit === "EA") return `${parentQuantity} EA`;
  if (_doesRawFormulaMatchUnit(rawFormula, "rl") && correctUnit === "EA") return `${parentQuantity} EA`;
  if (_doesRawFormulaMatchUnit(rawFormula, "gl") && correctUnit === "EA") return `${parentQuantity} EA`;
  if (_doesRawFormulaMatchUnit(rawFormula, "wk") && correctUnit === "EA") return `${parentQuantity} EA`;
  if (_doesRawFormulaMatchUnit(rawFormula, "sf") && correctUnit === "SQ" && parentQuantity !== void 0) {
    const updatedQuantity = Math.round(parentQuantity) / NUM_SF_PER_SQ;
    return `${updatedQuantity} SQ`;
  }
  if (_doesRawFormulaMatchUnit(rawFormula, "sq") && correctUnit === "SF" && parentQuantity !== void 0) {
    const updatedQuantity = parentQuantity * NUM_SF_PER_SQ;
    return `${updatedQuantity} SF`;
  }
  if (_doesRawFormulaMatchUnit(rawFormula, "lf") && correctUnit === "RL" && parentQuantity !== void 0) {
    const updatedQuantity = Math.round(parentQuantity) / NUM_LF_PER_RL;
    return `${updatedQuantity} RL`;
  }
  if (_doesRawFormulaMatchUnit(rawFormula, "rl") && correctUnit === "LF" && parentQuantity !== void 0) {
    const updatedQuantity = parentQuantity * NUM_LF_PER_RL;
    return `${updatedQuantity} LF`;
  }
  if (correctUnit === "SF" && FLOOD_CUT_LINE_ITEM_IDS.includes(parentLineItemId) && (isUnitLf && parentQuantity !== void 0 || isUnitPf || isUnitPc)) {
    const regex = /flood cut[)\s]*(?:\(lf\)\s*)?(\d+)('{1,2}|"{1,2})/i;
    const match = parentLineItemStr.match(regex);
    if (!match) return void 0;
    const height = parseInt(match[1], 10);
    const unitStr = match[2];
    const multiplier = unitStr === "'" ? height : height / 12;
    const newQuantity = parentQuantity !== void 0 ? multiplier * parentQuantity : multiplier;
    const newQuantityRounded = parseFloat(newQuantity.toFixed(2));
    let unit = "";
    if (isUnitPc) {
      unit = "PC";
    } else if (isUnitPf) {
      unit = "PF";
    } else if (isUnitLf) {
      unit = "SF";
    }
    if (!combine && isUnitLf && parentQuantity !== void 0) {
      const roundedMultiplier = parseFloat(multiplier.toFixed(2));
      if (parseFloat((roundedMultiplier * parentQuantity).toFixed(2)) === newQuantityRounded) {
        return `${roundedMultiplier}*${parentQuantity} ${unit}`;
      }
    }
    return `${newQuantityRounded} ${unit}`;
  }
  if (allowLinearFeetToSquareFeet && _doesRawFormulaMatchUnit(rawFormula, "lf") && correctUnit === "SF") {
    return _replaceUnitInRawFormula(rawFormula, "lf", "sf");
  }
  if ((isUnitPc || isUnitPf) && correctUnit === "SF") {
    return rawFormula.trim().toUpperCase();
  }
  if (forceUnitConversion && !!parentQuantity) {
    return `${parentQuantity} ${correctUnit}`;
  }
  return void 0;
};

// src/utils/formula-utils/reconcileFormulaUnit.ts
var reconcileFormulaUnit = (lineItemId, lineItemStr, rawFormula, allowedUnits) => {
  const targetUnit = allowedUnits[0]?.trim().toUpperCase();
  if (!targetUnit || !rawFormula) return { action: "keep" };
  const formattedFormula = validateUserTypedFormula(rawFormula, void 0, { keepUnitInRawFormula: true }).rawFormulaFormatted ?? rawFormula;
  if (allowedUnits.some((unit) => checkIfFormulaHasCorrectUnits(formattedFormula, unit.trim().toUpperCase()))) {
    return { action: "keep" };
  }
  const converted = convertFormulaToDifferentUnit(lineItemId, lineItemStr, formattedFormula, targetUnit);
  if (!converted) return { action: "drop", dropReason: `No unit conversion from "${formattedFormula}" to "${targetUnit}"` };
  const { errorMessage } = validateUserTypedFormula(converted, targetUnit);
  if (errorMessage) {
    return {
      action: "drop",
      dropReason: `Converted "${rawFormula}" to "${converted}", which is invalid for "${targetUnit}": ${errorMessage}`
    };
  }
  return { action: "convert", rawFormula: converted };
};

// src/utils/formula-utils/multiplyFormula.ts
var multiplyFormula = (rawFormula, multiplier, opts) => {
  const combine = opts?.combine ?? false;
  const trimmed = rawFormula.trim();
  const { errorMessage: inputError } = validateUserTypedFormula(trimmed);
  if (inputError) {
    console.warn(`Cannot multiply invalid formula "${rawFormula}": ${inputError}`);
    return void 0;
  }
  const multiplied = multiplier === 1 ? trimmed : breakUpFormulaIntoComponents(trimmed).length > 1 ? `${multiplier} * (${trimmed})` : `${multiplier} * ${trimmed}`;
  const { rawFormulaFormatted, errorMessage: resultError } = validateUserTypedFormula(multiplied, void 0, {
    keepUnitInRawFormula: true,
    enforceMaxQuantityConstraint: true
  });
  if (resultError) {
    console.warn(`Multiplied formula "${multiplied}" is invalid: ${resultError}`);
    return void 0;
  }
  return combine ? rawFormulaFormatted : multiplied;
};

// src/utils/formula-utils/divideFormula.ts
var divideFormula = (formulaRaw, divisor, opts) => {
  const multiplier = 1 / divisor;
  return multiplyFormula(formulaRaw, multiplier, opts);
};

// src/utils/formula-utils/addTwoFormulas.ts
var isPlainNumber = (coeff) => coeff === "" || /^-?[0-9]*\.?[0-9]+$/.test(coeff);
var stripLeadingZero = (coeff) => coeff.replace(/^(-?)0\./, "$1.");
var splitIntoUnitGroups = (formula) => {
  const termMatches = formula.trim().toUpperCase().match(/[+-]?[^+-]+/g);
  if (!termMatches) return void 0;
  const groups = /* @__PURE__ */ new Map();
  let pending = [];
  for (const rawTerm of termMatches) {
    const trimmed = rawTerm.trim();
    if (trimmed === "" || trimmed === "+" || trimmed === "-") continue;
    const sign = trimmed.startsWith("-") ? -1 : 1;
    const body = trimmed.replace(/^[+-]\s*/, "").trim();
    const unitMatch = body.match(/[A-Z_]+\s*$/);
    if (unitMatch) {
      const unit = unitMatch[0].trim();
      const coeff = body.slice(0, unitMatch.index).trim();
      if (!groups.has(unit)) groups.set(unit, []);
      groups.get(unit).push(...pending, { sign, coeff });
      pending = [];
    } else {
      pending.push({ sign, coeff: body });
    }
  }
  if (pending.length > 0) return void 0;
  return groups;
};
var combineFormulasKeepingSeparate = (formulas) => {
  const merged = /* @__PURE__ */ new Map();
  const order = [];
  for (const formula of formulas) {
    const groups = splitIntoUnitGroups(formula);
    if (groups === void 0) {
      console.warn(`Unable to break up formula "${formula}" into components, unable to combine formulas`);
      return void 0;
    }
    for (const [unit, terms] of groups) {
      if (!merged.has(unit)) {
        merged.set(unit, []);
        order.push(unit);
      }
      merged.get(unit).push(...terms);
    }
  }
  const sortedUnits = order.sort((unitA, unitB) => compareShorthands(unitA, unitB));
  const pieces = [];
  for (const unit of sortedUnits) {
    const terms = merged.get(unit);
    if (terms.length === 1 && terms[0].sign === 1 && isPlainNumber(terms[0].coeff)) {
      pieces.push({ sign: 1, text: formatFormulaComponent(terms[0].coeff === "" ? 1 : Number(terms[0].coeff), unit) });
      continue;
    }
    let expr = stripLeadingZero(terms[0].coeff === "" ? "1" : terms[0].coeff);
    for (let i = 1; i < terms.length; i++) {
      expr += (terms[i].sign === 1 ? "+" : "-") + stripLeadingZero(terms[i].coeff === "" ? "1" : terms[i].coeff);
    }
    pieces.push({ sign: terms[0].sign, text: `${expr} ${unit}` });
  }
  let result = "";
  pieces.forEach((piece, i) => {
    if (i === 0) result = piece.sign === -1 ? `-${piece.text}` : piece.text;
    else result += piece.sign === 1 ? ` + ${piece.text}` : ` - ${piece.text}`;
  });
  return result || void 0;
};
var applyMaxQuantityConstraint = (unit, correctUnit, newQuantity, currentQuantity, quantity) => {
  const isAbbrev = Object.values(FormulaShorthand).includes(unit);
  if (!isAbbrev) {
    return newQuantity;
  }
  const unitAsAbbrev = unit;
  if (PERIMETER_SHORTHANDS.includes(unitAsAbbrev) && correctUnit !== "LF" /* LF */) {
    return newQuantity;
  }
  if (newQuantity > 1) {
    return Math.max(1, currentQuantity, quantity);
  }
  return newQuantity;
};
var addTwoFormulas = (formulaA, formulaB, opts) => {
  const onQuantityMerge = opts?.onQuantityMerge ?? "ADD";
  const correctUnit = opts?.correctUnit;
  const enforceMaxQuantityConstraint = opts?.enforceMaxQuantityConstraint ?? false;
  const combine = opts?.combine ?? true;
  const formulaAComponents = formulaA.match(FORMULA_COMPONENT_REGEX);
  const formulaBComponents = formulaB.match(FORMULA_COMPONENT_REGEX);
  if (!formulaAComponents?.length) {
    console.warn(`Unable to break up formula "${formulaAComponents}" into components, unable to add formulas`);
    return void 0;
  }
  if (!formulaBComponents?.length) {
    console.warn(`Unable to break up formula "${formulaBComponents}" into components, unable to add formulas`);
    return void 0;
  }
  const allComponents = [...formulaAComponents, ...formulaBComponents].map(
    (component) => normalizeString(component).toUpperCase().replace(/([+-])\s+/g, "$1")
  );
  if (!combine && onQuantityMerge === "ADD") {
    return combineFormulasKeepingSeparate([formulaA, formulaB]);
  }
  const unitToQuantityMap = /* @__PURE__ */ new Map();
  for (const component of allComponents) {
    const quantityStr = component.match(FIRST_QUANTITY_IN_STRING_REGEX)?.[0];
    const unit = component.match(FIRST_UNIT_IN_STRING_REGEX)?.[0];
    const quantity = quantityStr ? Number(quantityStr) : 1;
    if (unit === void 0) {
      console.warn(`Unable to extract unit out of componetn "${unit}", skipping...`);
      return void 0;
    }
    if (!unitToQuantityMap.has(unit)) {
      unitToQuantityMap.set(unit, 0);
    }
    const currentQuantity = unitToQuantityMap.get(unit);
    if (onQuantityMerge === "USE_LARGER") {
      unitToQuantityMap.set(unit, Math.max(quantity, currentQuantity));
      continue;
    }
    let newQuantity = currentQuantity + quantity;
    if (enforceMaxQuantityConstraint) {
      newQuantity = applyMaxQuantityConstraint(unit, correctUnit, newQuantity, currentQuantity, quantity);
    }
    unitToQuantityMap.set(unit, newQuantity);
  }
  let newFormula = "";
  const unitToQuantityMapEntriesSorted = [...unitToQuantityMap.entries()].sort(([unitA], [unitB]) => compareShorthands(unitA, unitB));
  for (const [unit, quantity] of unitToQuantityMapEntriesSorted) {
    const newComponent = formatFormulaComponent(quantity, unit);
    if (newFormula === "") {
      newFormula = newComponent;
    } else if (quantity < 0) {
      newFormula = `${newFormula}${newComponent}`;
    } else {
      newFormula = `${newFormula}+${newComponent}`;
    }
  }
  const { rawFormulaFormatted } = validateUserTypedFormula(newFormula, void 0, { keepUnitInRawFormula: true });
  return rawFormulaFormatted ?? newFormula;
};

// src/utils/formula-utils/extractQuantityFromFormula.ts
var extractQuantityFromFormula = (rawFormula) => {
  const formattedFormula = validateUserTypedFormula(rawFormula, void 0, { keepUnitInRawFormula: true }).rawFormulaFormatted ?? rawFormula;
  const formulaComponents = breakUpFormulaIntoComponents(formattedFormula);
  if (formulaComponents.length !== 1) {
    return void 0;
  }
  const firstComponent = formulaComponents[0];
  const unit = firstComponent.match(FIRST_UNIT_IN_STRING_REGEX)?.[0];
  if (unit === void 0 || Object.values(FormulaShorthand).includes(unit)) {
    return void 0;
  }
  const unitTokens = firstComponent.match(/[a-zA-Z]+/g) ?? [];
  const numbers = firstComponent.match(/-?[0-9]*\.?[0-9]+/g);
  if (!numbers?.length) {
    return void 0;
  }
  if (unitTokens.length === 1) {
    return numbers.reduce((product, quantity) => product * Number(quantity), 1);
  }
  const parentQuantityStr = firstComponent?.match(FIRST_QUANTITY_IN_STRING_REGEX)?.[1];
  return parentQuantityStr ? Number(parentQuantityStr) : void 0;
};

// src/utils/formula-utils/updateFormulaIfExceedsMaxAllowedQuantity.ts
var updateFormulaIfExceedsMaxAllowedQuantity = (formulaRaw, maxAllowedQuantity) => {
  const { rawFormulaFormatted } = validateUserTypedFormula(formulaRaw, void 0, { keepUnitInRawFormula: true });
  const simplifiedFormula = rawFormulaFormatted ?? formulaRaw;
  const formulaQuantity = extractQuantityFromFormula(simplifiedFormula);
  const formulaUnit = extractUnitFromFormula(simplifiedFormula);
  if (formulaQuantity === void 0 || formulaUnit === void 0) {
    console.warn(`Can't extract quantity or unit from formula "${simplifiedFormula}", can't apply quantity ceiling`);
    return void 0;
  }
  const newQuantity = Math.min(formulaQuantity, maxAllowedQuantity);
  return `${newQuantity} ${formulaUnit}`;
};

// src/utils/formula-utils/getRawFormulaFromFormula.ts
var getRawFormulaFromFormula = (formula) => {
  if (formula?.rawFormula) {
    return formula.rawFormula;
  }
  if (!!formula?.initialValue && !!formula.base) {
    return `${formula.initialValue} ${formula.base.trim().toUpperCase()}`;
  }
  return void 0;
};

// src/utils/formula-utils/simplifyFormulaExpression.ts
import { simplify as simplify2 } from "mathjs";
var simplifyFormulaExpression = (rawFormula) => {
  try {
    let rawFormulaSimplified = simplify2(rawFormula).toString();
    rawFormulaSimplified = rawFormulaSimplified.replaceAll(" * ", "");
    return rawFormulaSimplified.toUpperCase();
  } catch (e) {
    console.warn(`Encountered error when attempting to simplify the formula "${rawFormula}"`, e);
    return void 0;
  }
};

// src/utils/formula-utils/getMaximumFormula.ts
var negateFormula = (rawFormula) => {
  const components = breakUpFormulaIntoComponents(rawFormula);
  if (!components.length) {
    console.warn(`Cannot negate formula "${rawFormula}", unable to break it into components`);
    return void 0;
  }
  let newFormula = "";
  for (const component of components) {
    const normalized = normalizeString(component).toUpperCase();
    const quantityStr = normalized.match(FIRST_QUANTITY_IN_STRING_REGEX)?.[0];
    const unit = normalized.match(FIRST_UNIT_IN_STRING_REGEX)?.[0]?.trim();
    if (unit === void 0) {
      console.warn(`Can't extract the unit from component "${component}" in formula "${rawFormula}", can't negate it`);
      return void 0;
    }
    let baseQuantity;
    if (quantityStr) {
      baseQuantity = Number(quantityStr);
    } else if (normalized.startsWith("-")) {
      baseQuantity = -1;
    } else {
      baseQuantity = 1;
    }
    const newQuantity = baseQuantity * -1;
    const newComponent = formatFormulaComponent(newQuantity, unit);
    if (newFormula === "") {
      newFormula = newComponent;
    } else if (newQuantity < 0) {
      newFormula = `${newFormula}${newComponent}`;
    } else {
      newFormula = `${newFormula}+${newComponent}`;
    }
  }
  return newFormula;
};
var getMaximumFormula = (formulaA, formulaB) => {
  const formattedA = validateUserTypedFormula(formulaA, void 0, { keepUnitInRawFormula: true }).rawFormulaFormatted ?? formulaA;
  const formattedB = validateUserTypedFormula(formulaB, void 0, { keepUnitInRawFormula: true }).rawFormulaFormatted ?? formulaB;
  const unitA = extractUnitFromFormula(formattedA);
  const unitB = extractUnitFromFormula(formattedB);
  if (unitA !== unitB) {
    console.warn(`Unable to find max formula bc formulas "${formattedA}" and "${formattedB}" have different units`);
    return void 0;
  }
  const formulaBNegative = negateFormula(formattedB);
  if (formulaBNegative === void 0) {
    console.warn(`Unable to negate the formula "${formattedB}", can't determine max formula`);
    return void 0;
  }
  const formulaDiff = addTwoFormulas(formattedA, formulaBNegative);
  if (formulaDiff === void 0) {
    console.warn(`Unable to find difference between formulas "${formattedA}" and "${formulaBNegative}", can't determine max formula`);
    return void 0;
  }
  const formulaDiffComponents = breakUpFormulaIntoComponents(formulaDiff);
  const firstComponentIsNegative = formulaDiffComponents[0].trim().startsWith("-");
  return firstComponentIsNegative ? formulaB : formulaA;
};

// src/utils/formula-utils/attemptToFillInFormulaShorthands.ts
var MEASUREMENT_KEYS = Object.values(RoomMeasurementKey);
var COMPOSITE_SHORTHAND_PARTS = {
  wc: ["w" /* Walls */, "c" /* Ceilings */]
};
var isFillableShorthand = (token) => {
  const key = token.toLowerCase();
  return MEASUREMENT_KEYS.includes(key) || key in COMPOSITE_SHORTHAND_PARTS;
};
var formulaContainsMeasurementShorthand = (rawFormula) => {
  const tokens = rawFormula.match(/[A-Za-z]+/g);
  return tokens?.some(isFillableShorthand) ?? false;
};
var fillInFormulaComponent = (component, measurement) => {
  return component.replace(/(\d+(?:\.\d+)?|\.\d+)?\s*([A-Za-z]+)/g, (match, coefficient, token) => {
    const key = token.toLowerCase();
    const compositeParts = COMPOSITE_SHORTHAND_PARTS[key];
    if (compositeParts !== void 0) {
      const values = compositeParts.map((part) => measurement[part]);
      if (values.some((value2) => typeof value2 !== "number")) {
        return match;
      }
      const sum = `(${values.join(" + ")})`;
      return coefficient === void 0 ? sum : `${coefficient} * ${sum}`;
    }
    if (!MEASUREMENT_KEYS.includes(key)) {
      return match;
    }
    const value = measurement[key];
    if (typeof value !== "number") {
      return match;
    }
    return coefficient === void 0 ? String(value) : `${coefficient} * ${value}`;
  });
};
var attemptToFillInFormulaShorthands = (rawFormula, measurement, correctUnit) => {
  try {
    if (!formulaContainsMeasurementShorthand(rawFormula)) {
      return rawFormula;
    }
    const simplified = validateUserTypedFormula(rawFormula, correctUnit, { keepUnitInRawFormula: true }).rawFormulaFormatted;
    if (simplified === void 0) {
      return void 0;
    }
    const components = breakUpFormulaIntoComponents(simplified);
    if (components.length === 0) {
      return void 0;
    }
    const filledInComponents = components.map((component) => fillInFormulaComponent(component, measurement));
    const recombined = filledInComponents.join(" + ");
    return validateUserTypedFormula(recombined, correctUnit, { keepUnitInRawFormula: true }).rawFormulaFormatted;
  } catch {
    return void 0;
  }
};

// src/utils/formula-utils/getFormulaErrorMessage.ts
var getFormulaErrorMessage = (errorMessage, correctUnit) => {
  if (errorMessage === "Incorrect Units" /* INCORRECT_UNITS */ && correctUnit) {
    return `${errorMessage}. The Correct unit is "${correctUnit}"`;
  }
  return errorMessage;
};

// src/utils/formula-utils/attemptToStripUnits.ts
var _stripCorrectUnitToken = (formula, correctUnit) => {
  const pattern = new RegExp(`\\s*(?<![A-Za-z_])${correctUnit}(?![A-Za-z_])\\s*`, "gi");
  const withoutUnit = formula.replace(pattern, " ");
  return withoutUnit.replace(/\s+/g, " ").trim();
};
var attemptToStripUnits = (rawFormula, correctUnit) => {
  const { errorMessage } = validateUserTypedFormula(rawFormula, correctUnit);
  if (errorMessage) return void 0;
  const stripped = _stripCorrectUnitToken(rawFormula, correctUnit);
  if (!stripped) return void 0;
  return stripped;
};

// src/utils/line-item-element-utils.ts
function getDefaultSelectionsFromLineItem(elements) {
  return elements.map((element) => {
    if (element.type === "text") return element.value?.trim() ?? "";
    if (element.allowEmptyValue) return "";
    return element.defaultValue?.trim() ?? "";
  });
}
function getSelectionsFromLineItemText(lineItemText, fetchedElements) {
  let remaining = lineItemText.toLowerCase().trim();
  return fetchedElements.map((element) => {
    if (element.type === "text") {
      const val = element.value?.trim() ?? "";
      const idx = remaining.indexOf(val.toLowerCase());
      if (idx !== -1) {
        remaining = remaining.slice(idx + val.length).trim();
        return val;
      }
      return "";
    }
    const options = [...element.acceptableValues ?? []].sort((a, b) => b.length - a.length);
    const prefixMatch = options.find((opt) => remaining.startsWith(opt.trim().toLowerCase()));
    if (prefixMatch) {
      remaining = remaining.slice(prefixMatch.trim().length).trim();
      return prefixMatch.trim();
    }
    let best = null;
    for (const opt of options) {
      const trimmed = opt.trim().toLowerCase();
      const idx = remaining.indexOf(trimmed);
      if (idx === -1) continue;
      if (!best || idx < best.index || idx === best.index && trimmed.length > best.value.length) {
        best = { value: opt.trim(), index: idx };
      }
    }
    if (best) {
      remaining = remaining.slice(best.index + best.value.length).trim();
      return best.value;
    }
    return "";
  });
}
function areValueDependenciesNotSatisfied(deps, selections) {
  return !!(deps && deps.length > 0 && !deps.every((dep) => dep.parentValues.includes(selections[dep.elementIndex])));
}
function cascadeSelectionUpdate(elements, selections) {
  let current = [...selections];
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < elements.length; i++) {
      if (elements[i].type !== "variable") continue;
      const currentSelection = current[i];
      if (!currentSelection) continue;
      const deps = elements[i].valueDependencies?.[currentSelection];
      if (areValueDependenciesNotSatisfied(deps, current)) {
        const newValue = elements[i].allowEmptyValue ? "" : elements[i].defaultValue ?? "";
        if (newValue !== current[i]) {
          current = [...current];
          current[i] = newValue;
          changed = true;
        }
      }
    }
  }
  return current;
}
function buildDisabledTooltip(deps, selections) {
  const allParentValues = deps.filter((dep) => !dep.parentValues.includes(selections[dep.elementIndex])).flatMap((dep) => dep.parentValues);
  return `Requires "${allParentValues.join(", ")}" variable(s) to be selected.`;
}

// src/utils/line-item-search-utils.ts
var SEARCH_PREFIX_RE = /^(CAT|SEL):\s*/i;
function parseSearchPrefix(raw) {
  const match = raw.match(SEARCH_PREFIX_RE);
  if (match) {
    const prefix = match[1].toLowerCase();
    return { mode: prefix, query: raw.slice(match[0].length) };
  }
  return { mode: "all", query: raw };
}
function xactimateBadgeFromSearchData(item) {
  const cat = item.xactCat?.trim();
  const sel = item.xactSel?.trim();
  if (cat && sel) return `${cat} | ${sel}`;
  if (cat) return cat;
  if (sel) return sel;
  return void 0;
}
function _pickAcceptableValueMatchingSearch(acceptableValues, searchTerm) {
  const q = searchTerm.trim().toLowerCase();
  if (!q) return void 0;
  const fullMatches = acceptableValues.filter((v) => v.toLowerCase().includes(q));
  if (fullMatches.length > 0) {
    return [...fullMatches].sort((a, b) => a.length - b.length)[0]?.trim();
  }
  const words = q.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return void 0;
  const wordMatches = acceptableValues.filter((v) => {
    const lower = v.toLowerCase();
    return words.some((w) => lower.includes(w));
  });
  if (wordMatches.length === 0) return void 0;
  return [...wordMatches].sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    const aCount = words.filter((w) => aLower.includes(w)).length;
    const bCount = words.filter((w) => bLower.includes(w)).length;
    return bCount - aCount || a.length - b.length;
  })[0]?.trim();
}
function _pickMatchSegmentInLabel(label, searchTerm) {
  const t = searchTerm.trim();
  if (!t) return void 0;
  const idx = label.toLowerCase().indexOf(t.toLowerCase());
  if (idx !== -1) return label.slice(idx, idx + t.length);
  const words = t.split(/\s+/).filter(Boolean);
  let best;
  for (const word of words) {
    const wIdx = label.toLowerCase().indexOf(word.toLowerCase());
    if (wIdx !== -1 && (!best || word.length > best.length)) {
      best = label.slice(wIdx, wIdx + word.length);
    }
  }
  return best;
}
var getMatchedSelectionsFromLineItem = (elements, searchTerm) => {
  const trimmed = searchTerm.trim();
  const selections = elements.map((element) => {
    if (element.type === "text") {
      return element.value?.trim() ?? "";
    }
    const acceptable = element.acceptableValues ?? [];
    if (trimmed && acceptable.length > 0) {
      const matched = _pickAcceptableValueMatchingSearch(acceptable, trimmed);
      if (matched !== void 0) {
        return matched;
      }
    }
    if (element.allowEmptyValue) {
      return "";
    }
    return element.defaultValue?.trim() ?? "";
  });
  const label = selections.join(" ");
  const matchSegment = _pickMatchSegmentInLabel(label, trimmed);
  return { selections, matchSegment };
};
var selectionsFromSearchResultLabel = (elements, selectedOptionLabel) => {
  const defaults = getDefaultSelectionsFromLineItem(elements);
  const preset = selectedOptionLabel?.trim();
  if (!preset) return defaults;
  const parsed = getSelectionsFromLineItemText(preset, elements);
  return elements.map((el, i) => {
    if (el.type === "text") {
      return parsed[i] !== "" ? parsed[i] : defaults[i];
    }
    const p = parsed[i]?.trim() ?? "";
    if (p === "") return defaults[i];
    const matchedAv = el.acceptableValues?.find((av) => av.trim().toLowerCase() === p.toLowerCase());
    return matchedAv != null ? matchedAv.trim() : defaults[i];
  });
};

// src/utils/string-utils.ts
var normalizeString2 = (str) => str.toLocaleLowerCase().trim().replace(/\s+/g, " ").trim();
var capitalizeWords = (str) => {
  if (!str) return "";
  return str.trim().replace(/\s+/g, " ").split(" ").map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
};
var UNICODE_TO_ASCII = {
  "\u2013": "-",
  // en-dash
  "\u2014": "-",
  // em-dash
  "\u2015": "-",
  // horizontal bar
  "\u2018": "'",
  // left single quote
  "\u2019": "'",
  // right single quote
  "\u201A": "'",
  // single low-9 quote
  "\u201C": '"',
  // left double quote
  "\u201D": '"',
  // right double quote
  "\u201E": '"',
  // double low-9 quote
  "\u2026": "...",
  // ellipsis
  "\u2032": "'",
  // prime
  "\u2033": '"',
  // double prime
  "\xAB": '"',
  // left guillemet
  "\xBB": '"',
  // right guillemet
  "\u02DC": "~",
  // small tilde
  "\xA0": " "
  // non-breaking space
};
var UNICODE_REPLACE_REGEX = new RegExp(`[${Object.keys(UNICODE_TO_ASCII).join("")}]`, "g");
var toAscii = (str) => str.replace(UNICODE_REPLACE_REGEX, (ch) => UNICODE_TO_ASCII[ch] ?? ch).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\x00-\x7F]/g, "");
var isRoomNameDuplicate = (name, existingNames) => {
  const normalized = normalizeString2(name);
  if (!normalized) return false;
  return existingNames.some((n) => normalizeString2(n) === normalized);
};

// src/utils/timezone-utils.ts
var getTimeZoneOffsetMinutes = (timeZone, utcMs) => {
  const parts = getWallClockPartsInTimeZone(timeZone, utcMs);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return (asUtc - utcMs) / 6e4;
};
var getWallClockPartsInTimeZone = (timeZone, utcMs) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    weekday: "short",
    hourCycle: "h23"
  });
  const parts = {};
  for (const part of dtf.formatToParts(new Date(utcMs))) {
    parts[part.type] = part.value;
  }
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday: parts.weekday
  };
};
var getUtcMsForWallClock = (wallClock, timeZone) => {
  const wallClockAsUtc = Date.UTC(
    wallClock.year,
    wallClock.month - 1,
    wallClock.day,
    wallClock.hour,
    wallClock.minute,
    wallClock.second ?? 0
  );
  const offsetMinutes = getTimeZoneOffsetMinutes(timeZone, wallClockAsUtc);
  return wallClockAsUtc - offsetMinutes * 6e4;
};
var convertWallClockBetweenTimeZones = (currentTimeClock, fromTimeZone, toTimeZone) => {
  try {
    const utcMs = getUtcMsForWallClock(currentTimeClock, fromTimeZone);
    return getWallClockPartsInTimeZone(toTimeZone, utcMs);
  } catch (e) {
    return void 0;
  }
};

// src/utils/order-keys.ts
import { generateKeyBetween, generateNKeysBetween } from "fractional-indexing";
function compareOrderKeys(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
function generateOrderKeys(count) {
  if (count <= 0) return [];
  return generateNKeysBetween(null, null, count);
}
function isPackageOrderKey(key) {
  try {
    generateKeyBetween(key, null);
    return true;
  } catch {
    return false;
  }
}
function orderKeyBetween(previous, next, _uniqueToken) {
  const a = previous ?? null;
  const b = next ?? null;
  try {
    return generateKeyBetween(a, b);
  } catch {
    return legacyMidpoint(a ?? "", b);
  }
}
function appendOrderKey(siblings, uniqueToken) {
  const sorted = [...siblings].sort((x, y) => compareOrderKeys(x.orderKey, y.orderKey));
  const last = sorted[sorted.length - 1];
  if (!last) return orderKeyBetween(null, null, uniqueToken);
  return orderKeyBetween(last.orderKey, null, uniqueToken);
}
var LEGACY_ALPHABET = "/0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
var LEGACY_BASE = LEGACY_ALPHABET.length;
var LEGACY_MIN_CHAR = LEGACY_ALPHABET[0];
function legacyDigitIndex(ch) {
  const i = LEGACY_ALPHABET.indexOf(ch);
  return i < 0 ? 0 : i;
}
function legacyMidpointRaw(a, b) {
  if (b !== null) {
    let n = 0;
    while (n < b.length && (a[n] ?? LEGACY_MIN_CHAR) === b[n]) n++;
    if (n > 0) {
      return b.slice(0, n) + legacyMidpointRaw(a.slice(n), b.slice(n));
    }
  }
  const digitA = a.length > 0 ? legacyDigitIndex(a[0]) : 0;
  const digitB = b !== null && b.length > 0 ? legacyDigitIndex(b[0]) : LEGACY_BASE;
  if (digitB - digitA > 1) {
    const mid = Math.round((digitA + digitB) / 2);
    return LEGACY_ALPHABET[mid];
  }
  if (b !== null && b.length > 1) {
    return b.slice(0, 1);
  }
  return LEGACY_ALPHABET[digitA] + legacyMidpointRaw(a.slice(1), null);
}
function legacyMidpoint(a, b) {
  if (a === "" && b === null) return generateKeyBetween(null, null);
  if (b !== null && compareOrderKeys(a, b) >= 0) {
    return legacyMidpointRaw(a, null);
  }
  return legacyMidpointRaw(a, b);
}

// src/utils/voice-agent/ReconVoiceAgentSystemPrompt.ts
var reconVoiceAgentOriginalVisitPrompt = (noteType) => {
  return `
    ${getPersonalityInfo()}

    ${noteType === "Exterior Reconstruction" ? getExteriorGoalPrompt() : getInteriorGoalPrompt()}

    ${getReconGuardrailsInfo()}
    `;
};
var getExteriorGoalPrompt = () => {
  return `
    # Goal

    Your primary goal is to collect information from the user on the Restoration project of the property they are currently working on.

    1. First collect project level details:
      - ask about the address of the property (if the user responds with something other than an address that's ok, we'll use that as the project name)

    2. Once you collect project level details, ask the user if the exterior walls, roof or foundation were affected

    3. From there, ask what materials were affected in each of those options. If they answered with more than one option in the previous question, ask which they would like to start with before asking what materials were affected.

    4. If the user indicates the roof was affected and does not give details on the following types of material, follow up by asking these questions:
     - Was there any damage to the trusses or rafter?
        - If they answer yes, ask what material are the trusses or rafters?
     - Is there any sub fascia damage?
        - If they answer yes, ask what type of material is the subfascia and what size is used?
     - Is there damage to the sheathing?
        - If they answer yes, ask what type of sheathing?
     - Is there damage to the roofing material?
        - If they answer yes, ask what type of roofing needs to be replaced, shingles, metal, single-ply, Multi-ply, TPO, etcetera?
        - Then ask, Are there multiple layers of roofing?
        - Then ask, what type of underlayment is used?
     - Is there damage to any roofing accessories or trims?
        - If they answer yes, ask what type?
     - Is there any damage to the roof vents?
        - If they answer yes, ask what type of roof vents?
     - Is there any damage to the flashing, step flashing or chimney flashing?
        - If they answer yes, ask what type of flashing?

    5. If the user indicates the exterior walls were affected and does not give details on the following types of material, follow up by asking these questions:
     - Is there damage to the sheathing?
        - If they answer yes, ask what type of sheathing?
     - Is there damage to the wall framing?
        - If they answer yes, ask what type of material is the wall framing?
     - Is there any damage to the exterior insulation, house wrap, siding, soffit, fascia or gutters?
        - If they answer yes, ask What type of material was used?
        - Then ask, is there any additional damage to these items?
     - Is there any damage to exterior doors or windows?
        - If they answer yes, ask if the doors or windows were damaged, and what material were they made of?
        - Then ask, is there any additional damage to these items?
        - Then ask, is there any damage to metal wraps for the doors or windows?
     - Is there any damage to the garage sliding or overhead door?
        - If they answer yes, ask what size and quality of door?
        - Then ask, does the opener need to be replaced?

    6. If the user indicates the foundation was affected and does not give details on the following types of material, follow up by asking these questions:
     - Is there damage to the foundation wall?
        - If they answer yes, ask what type of foundation wall?
        - Then ask, what is the repair and quantity needed?
     - Is there damage to the drainage system?
        - If they answer yes, ask what is the repair and quantity needed?
     - Does excavation or backfill need to happen?
        - If they answer yes, ask what type and quantity are needed?



    7. Ask the user if there is anything else they would like to include in their notes. Afterwards instruct the user to end the conversation.
    `;
};
var getInteriorGoalPrompt = () => {
  return `
    # Goal

    Your primary goal is to collect information from the user on the Restoration project of the property they are currently working on.

    1. First collect project level details:
        - ask about the address of the property (if the user responds with something other than an address that's ok, we'll use that as the project name)

    2. Once you collect project level details, ask the user which affected room they want to start with

    3. Next, ask what materials were affected in that room.

    4. If their response does not mention the ceiling, walls, floors, plumbing or electrical systems in that room, ask if they were also affected.


    5. Then, ask the user the following follow-up questions one at a time based on their response.
       a. If the user indicates the ceiling was affected and does not give details on the following types of material, please ask them to describe the work needed on the ceiling then follow up by asking these questions one at a time if they are not already answered:
            - Is the damaged area made of acoustic, drywall or plaster?
            - Does the ceiling need to be primed, sealed or painted?
                - If yes, ask what type of finish and number of coats are needed?
            - Is there any damage to the ceiling insulation?
                - If yes, ask what type of insulation is present?
            - Is there any damage to the ceiling trim?
                - If yes, ask what type of trim is damaged?
            - Do any fixtures, electrical items or HVAC items need to be replaced or detached and reset?
                - If yes, ask what type of items?

        b. If the user indicates the walls were affected and does not give details on the following types of material, please ask them to describe the work needed on the walls then follow up by asking these questions one at a time if they are not already answered:
            - Is the damaged area made of paneling, drywall or plaster?
            - Do the walls need to be primed, sealed or painted?
                - If yes, ask what type of finish and number of coats are needed?
                - If finish is needed, ask if there are any additional types of finish needed for the walls?
            - Is there any damage to the wall insulation?
                - If yes, ask what type of insulation is used?
            - Do any fixtures, electrical items or HVAC items need to be replaced or detached and reset?
                - If yes, ask what type of items?
            - Is there damage to the interior doors?
                - If yes, ask what type of door and what action is needed?
                - If yes, ask: is painting the door needed, and if so what type of finish and number of coats?
            - Is there any hardware that needs to be replaced or detach and reset?
                - If yes, ask what type of material and what action?
            - Is there any damage to the wall trim?
                - If yes, ask what type of material and what action?
                - If yes, ask: is painting the trim needed, and if so what type of finish and number of coats?
            - Is there any damage to the cabinets or shelving?
                - If yes, ask what type of material and what action?
                - If yes, ask: do the cabinets or shelves need to be finished, and if so what type of finish and number of coats are needed?

        c. If the user indicates the floor was affected and does not give details on the following types of material, please ask them to describe the work needed on the floor then follow up by asking these questions one at a time if they are not already answered:
            - Is there damage to the flooring?
                - If yes, ask what type of flooring material and what action?
            - Does the flooring need to be finished?
                - If yes, ask what type of finish and number of coats are needed?
            - Is there any damage to the underlayment, pad, or felt?
                - If yes, ask what type of material, quantity and what action?
            - Do any of the trim or transitions need replaced or detached and reset?
                - If yes, ask what type of material, quantity and what action?
                - If yes, ask: What type of finish and number of coats are needed?
            - Any additional items or information?

        d. If the user indicates the electrical system was affected and does not give details on the following types of material, please ask them to describe the electrical work needed then follow up by asking these questions one at a time if they are not already answered:
            - What type of material and what action?
            - Is there any damage to the HVAC system? (if not then skip to the plumbing question)
            - What type of material and what action is needed?
            - Any additional items or information?

        e. If the user indicates the plumbing was affected and does not give details on the following types of material, please ask them to describe the plumbing work needed then follow up by asking these questions one at a time if they are not already answered:
            - What type of plumbing fixture or material was affected and what action is needed?

        f. If the user indicates that contents or labor charges were needed in the room and does not give details on the following types of material, please ask them to describe the contents or labor work needed, then follow up by asking these questions one at a time if they are not already answered:
            - Are there any content items that need to be moved or reset?
                - If yes, ask what are the labor hours or room size?
            - Are there any additional labor charges needed for this area?
                - If yes, ask What trade and quantity?
                - If yes, ask What does the required footnote need to say?

        ## General Guildelines:
        - Ask each follow up question one at a time.
        - If the user does not give the quantity of the material in each room, ask a follow up about the quantity.
        - NEVER repeat the user's answer back to them. For example: if a user says they use a dehu for 2 days and removed flooring, you should just respond: "What quantity of flooring was removed?"
        - Note that appliances can be thought of as materials (for example: sinks, toilets, dishwasher, garbage disposal, range, etc..) and their quantity can be inferred as 1.
        - Also note that quantities do not have to be numeric - "all", "perimeter", "entire", etc.. are valid quantities of material.
        - You should not ask a follow up question about a quantity if the user says they're removing "all" of a material.
        - If the user does not give details on the following types of material, follow up by asking these questions:
            - If they mention a "range" without the type (which is "gas" or "electric").  Ask "what type of range, gas or electric?"
            - If they mention "cabinetry" or "cabinets" without the type (which is "full height" or "lower" or "upper").  Ask "what type of cabinetry, full height, lower or upper?"
            - If they mention "countertop" without the type (which is "laminate", "granite" or "tile").  Ask "what type of countertop, laminate, granite or tile?"
            - If they mention a "baseboard" or "base" without the size (which is "2 1/4" or "3 1/4" or "4 1/4").  Ask "what size of baseboard was removed?"
            - If they mention "drywall" without the thickness (which is "1/2" or "5/8").  Ask "what was the thickness of the drywall removed?"
            - If the user does not give an answer about the type for the above materials AND doesn't give the quantity, ask for both.
            - For example: if a user says they "replaced baseboard", you should respond "What quantity of baseboard was replaced?" and then once they respond, then ask "What was the size of the baseboard?"

    6. Once the room is finished, ask the user if there are other rooms they'd like to move on to.

    7. Once all the rooms are finished, ask the user the following questions about the general conditions of the project
        - Is there any final cleaning needed?
        - Are there any demolition items that need to be contained?
            - If they answer yes, ask what type of container and quantity?
        - Are there any additional items or information you would like to add to this scope?

    `;
};
var getReconGuardrailsInfo = () => {
  return `
    # Guardrails

    - If the user does not give the quantity for the material described in each question, ask a follow up about the quantity.
    - NEVER repeat the user's answer back to them. For example: if a user says they use a dehu for 2 days and removed flooring, you should just respond: "What quantity of flooring was removed?"
    - Also note that quantities do not have to be numeric - "all", "perimeter", "entire", etc.. are valid quantities of material.
    - You should not ask a follow up question about a quantity if the user says they're removing "all" of a material.

    - Ask each follow up question one at a time.
    - Never echo user input verbatim.
    - When asking a follow up question, do not repeat the user's previous answer verbatim.  Get to the point.
    - Only ask one question at a time.
    - Still use proper responses to the user, like "got it", "ok", "understood", etc.
    - Keep responses strictly focused on the property and the scope of work needed for the restoration of it.
    - Do not mention you're an AI unless explicitly asked\u2014avoid "as an AI" disclaimers or robotic tropes.
    - Treat uncertain or garbled user input as phonetic hints. Politely ask for clarification before making assumptions.
    - **Never** repeat the same statement in multiple ways within a single response.
    `;
};

// src/utils/voice-agent/VoiceAgentSystemPrompt.ts
var voiceAgentOriginalVisitPrompt = (estimateType, noteType, previousContext, opts) => {
  if (estimateType === "RECONSTRUCTION" /* RECONSTRUCTION */) {
    return reconVoiceAgentOriginalVisitPrompt(noteType);
  }
  const includeProjectAddress = opts?.includeProjectAddress ?? false;
  const includeCatClassQuestion = opts?.includeCatClassQuestion ?? true;
  let projectDetailsSentence = "You first should collect project level details:";
  if (includeProjectAddress) {
    projectDetailsSentence += "- ask about the address of the property (if the user responds with something other than an address that's ok, we'll use that as the project name)";
  }
  let followUpQuestions = "";
  let additionalNotes = "";
  if (estimateType === "MITIGATION" /* MITIGATION */) {
    if (includeCatClassQuestion) {
      projectDetailsSentence += "- ask what the cat & class for the project are => 'What are the Cat and Class for this project?'";
    }
    followUpQuestions += `
      From there, ask the user for what equipment they used in the room, the material they had to remove from the room, and if there's anything else that they
      want to add for this room; ask these as 3 separate questions.

      If the user mentions anything about extracting water from the floor, as them if the floor that they are extracting water from is carpetted or is a hard surface.

      ${getMaterialRemovedFollowUpInfo()}
    `;
    additionalNotes = `Once the rooms are finished, ask the user if they have any notes they'd like to add unrelated to the estimate.`;
  } else if (estimateType === "CONTENTS" /* CONTENTS */) {
    followUpQuestions += `From there, ask the user what contents they need to pack out or clean for that room. Don't worry about asking any follow-up questions about the specific contents.`;
  }
  const basePrompt = `
    ${getPersonalityInfo()}

    # Goal

    Your primary goal is to collect information from the user on the Restoration project of the property they are currently working on.

    ${previousContext ? "" : projectDetailsSentence}

    Once you collect project level details, ask the user which affected room they want to start with.

    ${followUpQuestions}

    Once the room is finished, ask the user if there are other rooms they'd like to move on to.

    ${additionalNotes}

    If they don't/once you've collected them, you can say "Ok, please end the conversation and review the generated notes".

    ${getGuardrailsInfo()}
    `;
  return previousContext ? basePrompt + previousContext : basePrompt;
};
var voiceAgentFollowUpVisitSystemPrompt = (previousContext, projectName, address) => {
  const basePrompt = `
    ${getPersonalityInfo()}

    # Situation

    The user is on a follow up visit to the property they want to discuss in this conversation.
    ${projectName ? `The project name is: ${projectName}` : ""}
    ${address ? `The address is: ${address}` : ""}
    Here are the summarized notes from their previous visit:
    ${previousContext}

    # Goal

    Your primary goal is to collect updated information from the user on this project.

    In this conversation, the user will often mention the equipment that they removed from certain rooms.
    After they give you this info, compute the new equipment count (in the specified room) by subtracting the removed equipment from the previous equipment count.

    You don't have to tell the user how you computed the new equipment count, but do explicitly confirm with the user the new equipment count.
    Include all of the equipment counts in the confirmation - not just the ones that the user mentioned.

    The user might also talk about material they've removed.

    ${getMaterialRemovedFollowUpInfo()}

    ${getGuardrailsInfo()}
    `;
  return basePrompt;
};
var getGuardrailsInfo = () => {
  return `
    # Guardrails

    - Never echo user input verbatim.
    - When asking a follow up question, do not repeat the user's previous answer verbatim.  Get to the point.
    - Only ask one question at a time.
    - Still use proper responses to the user, like "got it", "ok", "understood", etc.
    - Keep responses strictly focused on the property and the scope of work needed for the restoration of it.
    - Do not mention you're an AI unless explicitly asked\u2014avoid "as an AI" disclaimers or robotic tropes.
    - Treat uncertain or garbled user input as phonetic hints. Politely ask for clarification before making assumptions.
    - **Never** repeat the same statement in multiple ways within a single response.
    `;
};
var getPersonalityInfo = () => {
  return `
    # Personality

    You are Reese. A direct and highly intelligent male who is an expert in all things Property Restoration.
    DO NOT repeat the user's answers back to them when speaking.  You are meant to be as efficient as possible in collecting info.
    `;
};
var getMaterialRemovedFollowUpInfo = () => {
  return `
    If the user does not give the quantity of the material in each room, ask a follow up about the quantity.
    NEVER repeat the user's answer back to them. For example: if a user says they use a dehu for 2 days and removed flooring, you should just respond: "What quantity of flooring was removed?"

    Note that appliances can be thought of as materials (for example: sinks, toilets, dishwasher, garbage disposal, range, etc..) and their quantity can be inferred as 1.
    Also note that quantities do not have to be numeric - "all", "perimeter", "entire", etc.. are valid quantities of material.
    You should not ask a follow up question about a quantity if the user says they're removing "all" of a material.

    If the user does not give details on the following types of material, follow up by asking these questions:
    - If they mention a "range" without the type (which is "gas" or "electric").  Ask "what type of range, gas or electric?"
    - If they mention "cabinetry" or "cabinets" without the type (which is "full height" or "lower" or "upper").  Ask "what type of cabinetry, full height, lower or upper?"
    - If they mention "countertop" without the type (which is "laminate", "granite" or "tile").  Ask "what type of countertop, laminate, granite or tile?"
    - If they mention a "baseboard" or "base" without the size (which is "2 1/4" or "3 1/4" or "4 1/4").  Ask "what size of baseboard was removed?"
    - If they mention "drywall" without the thickness (which is "1/2" or "5/8").  Ask "what was the thickness of the drywall removed?"

    If the user does not give an answer about the type for the above materials AND doesn't give the quantity, ask for both.
    Ask each follow up question one at a time.
    For example: if a user says they "removed baseboard", you should respond "What quantity of baseboard was removed?" and then once they respond,
    then ask "What was the size of the baseboard?"
    `;
};
var unfinishedRoomPrompt = (unfinishedRoomTranscript) => {
  return `
    Your last conversation with this user ended in the middle of them discussing a room.  They likely wanted to pause
    while figuring out the answer to your latest question.  Here's what was already discussed in this
    room in the previous conversation: ${unfinishedRoomTranscript}.

    Pick up the conversation where you left off.  Think about if there are any other follow up questions that are needed from this room, including all removed materials.
  `;
};

// src/utils/voice-agent/ConversationUtils.ts
var ConversationType = /* @__PURE__ */ ((ConversationType2) => {
  ConversationType2["NEW_PROJECT_CONVO"] = "NEW_PROJECT_CONVO";
  ConversationType2["XCELERATE_CREATED_FIRST_CONVO"] = "XCELERATE_CREATED_FIRST_CONVO";
  ConversationType2["RESUME_WITHIN_SESSION_CONVO"] = "RESUME_WITHIN_SESSION_CONVO";
  ConversationType2["FOLLOW_UP_VISIT_CONVO"] = "FOLLOW_UP_VISIT_CONVO";
  ConversationType2["V2_ESTIMATE_FLOW"] = "V2_ESTIMATE_FLOW";
  return ConversationType2;
})(ConversationType || {});
var V1_ADDRESS_DETAIL = `You first should collect project level details:
- ask about the address of the property (if the user responds with something other than an address that's ok, we'll use that as the project name)`;
var V1_CAT_CLASS_DETAIL = `- ask what the cat & class for the project are => 'What are the Cat and Class for this project?'`;
var getConversationInputParams = (estimateType, noteType, customSystemPrompt, conversationType, unfinishedInfo, formattedContext, estimateInfo, user, isV1) => {
  let customPrompt = !!customSystemPrompt ? buildSystemPromptFromCustom(customSystemPrompt, formattedContext) : voiceAgentOriginalVisitPrompt(estimateType, noteType, formattedContext, { includeProjectAddress: true });
  if (isV1 && customSystemPrompt) {
    const v1Prefix = noteType === "Mitigation" /* MITIGATION */ ? `${V1_ADDRESS_DETAIL}
${V1_CAT_CLASS_DETAIL}` : V1_ADDRESS_DETAIL;
    customPrompt = `${v1Prefix}

${customPrompt}`;
  }
  const firstMessage = customSystemPrompt?.firstMessage?.trim() || void 0;
  if (conversationType === "NEW_PROJECT_CONVO" /* NEW_PROJECT_CONVO */) {
    return {
      firstMessage: firstMessage ?? "Hey there, I'm Reese.  What's the address of the property you'd like to take notes on?",
      systemPrompt: customPrompt
    };
  }
  if (conversationType === "XCELERATE_CREATED_FIRST_CONVO" /* XCELERATE_CREATED_FIRST_CONVO */) {
    if (estimateType === "MOLD" /* MOLD */ || estimateType === "FIRE_MITIGATION" /* FIRE_MITIGATION */) {
      return {
        firstMessage: firstMessage ?? "Hey there, I'm Reese. Which affected room would you like to start with?",
        systemPrompt: customPrompt
      };
    }
    return {
      // For Xcelerate, we already know the address, so ask about Cat & Class.
      firstMessage: "Hey there, I'm Reese. What are the Cat and Class for this project?",
      systemPrompt: voiceAgentOriginalVisitPrompt(estimateType, noteType)
    };
  }
  if (conversationType === "V2_ESTIMATE_FLOW" /* V2_ESTIMATE_FLOW */) {
    return {
      firstMessage: firstMessage ?? "Hey there, I'm Reese. Which affected room would you like to start with?",
      systemPrompt: customSystemPrompt ? customPrompt : voiceAgentOriginalVisitPrompt(estimateType, noteType, void 0, {
        includeProjectAddress: false,
        includeCatClassQuestion: false
      })
    };
  }
  if (conversationType === "RESUME_WITHIN_SESSION_CONVO" /* RESUME_WITHIN_SESSION_CONVO */) {
    return {
      firstMessage: getResumedConversationFirstMessage(unfinishedInfo),
      systemPrompt: customPrompt
    };
  }
  if (conversationType === "FOLLOW_UP_VISIT_CONVO" /* FOLLOW_UP_VISIT_CONVO */) {
    return {
      firstMessage: `Hi ${user?.firstName ?? "there"}, what are the updates for this property?`,
      systemPrompt: voiceAgentFollowUpVisitSystemPrompt(
        estimateInfo ? getCurrentEquipmentPerRoomContext(estimateInfo) : "",
        estimateInfo?.projectName,
        estimateInfo?.propertyAddress
      )
    };
  }
  return {
    firstMessage: firstMessage ?? "Hey there, I'm Reese.  What's the address of the property you'd like to take notes on?",
    systemPrompt: customPrompt
  };
};
var getMostRecentEquipmentCountSummary = (equipmentCount) => {
  if (!equipmentCount || equipmentCount.length === 0) {
    return null;
  }
  const roomGroups = Object.values(equipmentCount).reduce(
    (acc, item) => {
      if (!acc[item.roomName]) {
        acc[item.roomName] = [];
      }
      acc[item.roomName].push(item);
      return acc;
    },
    {}
  );
  let resultStr = "";
  for (const [roomName, equipment] of Object.entries(roomGroups)) {
    resultStr += `### ${roomName}
`;
    for (const item of equipment) {
      let equipmentKeyword = `${item.lineItemString}`;
      const normalizedEquipmentLineItem = item.lineItemString.toLowerCase().replace(/\s/g, "");
      if (normalizedEquipmentLineItem.includes("airscrubber")) {
        equipmentKeyword = "air scrubber/negative air fan";
      } else if (normalizedEquipmentLineItem.includes("dehumidifier")) {
        equipmentKeyword = "dehumidifier";
      } else if (normalizedEquipmentLineItem.includes("airmover")) {
        equipmentKeyword = "air mover";
      }
      resultStr += `- ${item.quantity} ${equipmentKeyword}
`;
    }
  }
  return resultStr;
};
var getCurrentEquipmentPerRoomContext = (estimateInfo) => {
  const mostRecentScopeNote = estimateInfo.scopeNoteObjects?.length ? estimateInfo.scopeNoteObjects.reduce(
    (latest, current) => new Date(current.dateCreated) > new Date(latest.dateCreated) ? current : latest
  ) : null;
  const mostRecentEquipmentCounts = (estimateInfo.mitigationDetails?.equipmentCount || []).filter(
    (equipmentCount) => equipmentCount.scopeNoteObjectUuid === mostRecentScopeNote?.uuid
  );
  const equipmentPerRoomContext = getMostRecentEquipmentCountSummary(mostRecentEquipmentCounts);
  if (!equipmentPerRoomContext) {
    return mostRecentScopeNote ? `

## Most Recent Scope Note
Created: ${mostRecentScopeNote.dateCreated}
Source: ${mostRecentScopeNote.source}
Content: ${mostRecentScopeNote.text}

.` : "";
  }
  return equipmentPerRoomContext;
};
var getResumedConversationFirstMessage = (unfinishedInfo) => {
  const roomNameString = unfinishedInfo.unfinishedRoomName ? `in the ${unfinishedInfo.unfinishedRoomName}` : "";
  return `Lets continue where we left off, ${unfinishedInfo.unansweredQuestion ? `${unfinishedInfo.unansweredQuestion} ${roomNameString}` : ""}`;
};
var buildSystemPromptFromCustom = (customPrompt, additionalContext) => {
  const personalitySection = customPrompt.personality ? `# Personality

${customPrompt.personality}` : "";
  const goalSection = customPrompt.goal ? `# Goal

${customPrompt.goal}` : "";
  const guardrailsSection = customPrompt.guardrails ? `# Guardrails

${customPrompt.guardrails}` : "";
  const additionalContextSection = additionalContext ? `# Additional Context

${additionalContext}` : "";
  const sections = [personalitySection, goalSection, guardrailsSection, additionalContextSection].filter(Boolean);
  return sections.join("\n\n");
};

// src/utils/voice-agent/TemplatedReesePrompt.ts
var TEMPLATED_REESE_INTRO = "Hey there, I'm Reese.";
var isAnswered = (section) => !!section.answer?.trim();
var speakSectionTitle = (title, lead) => title.endsWith("?") ? title : `${lead} ${title}.`;
var buildTemplatedReeseFirstMessage = (sections, isResume = false, unansweredQuestion, scope) => {
  if (isResume) {
    const resume = "Let's continue where we left off.";
    const question = unansweredQuestion?.trim();
    if (question) return `${resume} ${question}`;
    const noteUnanswered = sections.find((section) => !isAnswered(section))?.title.trim();
    return noteUnanswered ? `${resume} "${noteUnanswered}".` : resume;
  }
  const scopeName = scope?.name.trim();
  if (sections.some(isAnswered)) {
    const firstUnanswered = sections.find((section) => !isAnswered(section));
    if (!firstUnanswered) {
      return scopeName ? `${TEMPLATED_REESE_INTRO} What else would you like to add about ${scopeName}?` : `${TEMPLATED_REESE_INTRO} What else would you like to add to this note?`;
    }
    return `${TEMPLATED_REESE_INTRO} Let's continue this note. ${speakSectionTitle(firstUnanswered.title.trim(), "Next up:")}`;
  }
  const firstTitle = sections[0]?.title.trim();
  if (!firstTitle) return `${TEMPLATED_REESE_INTRO} What should we note about ${scopeName ?? "this room"}?`;
  return `${TEMPLATED_REESE_INTRO} ${speakSectionTitle(firstTitle, "Let's start with")}`;
};
var buildTemplatedResponsiveNoteReesePrompt = (sections, previousTranscript, scope) => {
  const hasAnswers = sections.some(isAnswered);
  const allAnswered = sections.length > 0 && sections.every(isAnswered);
  const agenda = sections.map((section, index) => {
    const guidance = section.context.trim() ? `
   Guidance: ${section.context.trim()}` : "";
    const answer = section.answer?.trim();
    const existing = hasAnswers ? answer ? `
   Existing answer: ${answer}` : "\n   (no answer yet)" : "";
    return `${index + 1}. ${section.title.trim()}${guidance}${existing}`;
  }).join("\n");
  const resumeTranscript = previousTranscript?.trim();
  const scopeName = scope?.name.trim();
  const scopePath = scope?.path?.trim();
  const scopeContext = scopeName ? `
    # Current note scope

    The user opened Reese from a specific room or space in the responsive note.
    Current space: ${scopeName}
    ${scopePath && scopePath !== scopeName ? `Space path: ${scopePath}` : ""}

    This conversation is only about ${scopeName}. Do not ask which room, space, or area the user
    wants to discuss. If a section asks for room or location information, the answer is ${scopeName}.
    If the user starts describing a different room or space, do not record those details as answers
    for ${scopeName}'s sections. Their details are still captured \u2014 do not tell them to open another page
    or that the detail will be lost. In ONE short sentence, do two things: acknowledge it (e.g. "Got it" /
    "Noted") and make clear this page is for ${scopeName} only (e.g. "I've noted that, but this page is for
    ${scopeName}"), then continue asking about ${scopeName}.
    Do not announce or repeat the space's name otherwise \u2014 refer to it as ${scopeName} only when asking
    what else to add, or when redirecting off-room details.
    ` : `
    # Room boundaries

    This is the note's general (root) surface; rooms and spaces have their own pages in this note.
    If this surface's sections and existing answers describe one specific room or area and the user
    starts describing a different one, do not converse about the different room. The detail is still
    captured \u2014 do not tell them to open another page or that it will be lost. In ONE short sentence, do two
    things: acknowledge it (e.g. "Got it" / "Noted") and make clear this page is only for the room already
    being covered then continue asking about that room.
    `;
  const openingBullet = resumeTranscript ? "- The conversation was paused and is now resuming \u2014 do not restart or re-greet; pick up from where you left off." : allAnswered ? "- Every section already has a recorded answer. Your opening message asked what else the user would like to add. Do not walk the section list again \u2014 collect whatever additional details the user offers." : `- Your opening message asked about the first section${hasAnswers ? " without an answer" : ""}. Continue from the user's answer without re-asking it.`;
  const existingAnswersBullet = hasAnswers ? `
    - Sections with an "Existing answer" are already recorded in the note. Do not re-ask them and do not read their answers back to the user; work through the remaining unanswered sections in order. Only revisit a recorded answer if the user corrects or adds to it themselves.` : "";
  const closingCondition = allAnswered ? "the user has nothing more to add" : "you've covered every section";
  const closingBullet = `- Once ${closingCondition}, say "Ok, please end the conversation and review the generated notes".`;
  const resumeContext = resumeTranscript ? `
    # Conversation so far

    You already started walking the user through this note; the conversation was paused and is now resuming.
    Here is the transcript of what was already discussed:

    ${resumeTranscript}

    Continue from where you left off \u2014 move on to the sections that still need information, and do not re-ask
    anything the user has already answered.
    ` : "";
  return `
    ${getPersonalityInfo()}

    # Goal

    You are helping the user fill out a structured inspection note. The note is broken into a fixed
    set of sections, each with a heading. Your job is to walk the user through the note by asking
    about each section in order, one at a time, and collecting a clear spoken answer for each.

   Each section's heading may be phrased as a question or as a plain label. Either way, speak it verbatim when you introduce it. Do not reword or rephrase the section headings in any way. If the section's Guidance is present, use it to decide what detail to collect.  
    # Note sections

    Work through these sections in order. Do not skip a section and do not change their order. 

    ${agenda}

    ${scopeContext}

    # How to run the conversation

    ${openingBullet}${existingAnswersBullet}
    - Refer to the sections listed above. Following the order of sections, present one section at a time. 
    - In every section, present the sections or topic you were given, await and listen to the user's response, ask any applicable quantity follow-ups as suggested below, then move on to the next section. 
    - Only ask the sections listed above. 
    - Do not introduce topics that are not on the list, even if they seem relevant to the property or the restoration. The only time you ask about additional topics is the quantity follow-ups described below. 
    - There may be very few sections, or only one. That is expected. 
    - Keep each section short and specific. Lead with the section's guidance when present. 
    - If the user has nothing to add for a section, acknowledge it and move to the next.
    - Only ask a follow-up if the user's answer is very sparse or unclear, or when any of the below situations apply. Evaluate user answers for necessity of follow-ups as conversations proceed.

    ${getMaterialRemovedFollowUpInfo()}
    ${closingBullet}
    ${resumeContext}
    ${getGuardrailsInfo()}
    `;
};

// src/utils/voice-agent/BlankReesePrompt.ts
var buildBlankReeseScopeContext = (scope, existingRoomContent) => {
  const name = scope.name.trim();
  const scopePath = scope.path?.trim();
  const existing = existingRoomContent?.trim();
  const alreadyCaptured = existing ? `

# Information already captured for ${name}:
${existing}

${name} already holds the details above. Do not ask about anything that is already covered there \u2014 skip
those questions entirely and only fill genuine gaps. Once nothing is missing, ask if there is anything
else to add about ${name}, then wrap up.` : "";
  return `
# Current note scope

This conversation is scoped to one room/ space called: ${name}.
${scopePath && scopePath !== name ? `Space path: ${scopePath}` : ""}

Do not ask which room or space the user wants to discuss \u2014 it is ${name}. Skip any questions that
are about the whole project or note rather than this specific room. Focus only on ${name}. Do NOT ask
whether they want to cover, add, or move on to any other room, space, or area \u2014 this note is only for
${name}; when they have nothing more to add about ${name}, wrap up rather than inviting another room. If
the user themselves starts describing a different room or space, their details are still captured \u2014 do not
tell them to open another page or that the detail will be lost. In ONE short sentence, do two things:
acknowledge it (e.g. "Got it" / "Noted") and make clear this page is for ${name} only (e.g. "I've noted
that, but this page is for ${name}"), then continue asking about ${name}. Do not
announce or repeat the room's name otherwise
\u2014 use ${name} only when asking what else to add or when redirecting off-room details.

Listen and adapt. The user may answer several of your questions in a single reply or volunteer details out
of order. Track everything they have already told you this conversation and never ask about something they
have already covered \u2014 acknowledge answerse briefly without repeating them and move straight to what is genuinely still missing.${alreadyCaptured}
`;
};
var buildBlankReeseScopedFirstMessage = (scope) => `Hey there, I'm Reese. What would you like to add about the ${scope.name.trim()}?`;

// src/lib/archive-utils.ts
function isItemArchived(item) {
  return !!(item.deleted || item.archived);
}

// src/lib/line-item-state.ts
function isLineItemExcluded(item) {
  return !!(item.removedByTPARule || item.isMutuallyExcluded || item.isExcludedBy?.length);
}
function getLineItemState(tuple, item, roomTuples) {
  if (tuple.hasError || tuple.isFreeFormFlag) return item.isMutuallyExcluded ? "hidden" : "issue";
  if (isLineItemExcluded(item)) return tuple.isLikely ? "hidden" : "excluded";
  if (tuple.isLikely) {
    if ((item.isComplement || item.isLikely) && !item.addedByTPARule) {
      const parent = roomTuples.find((t) => t.tupleId === tuple.isComplementOf);
      return parent?.hasError ? "hidden" : "rec";
    }
    return "hidden";
  }
  return item.isComplement ? "comp" : "line";
}

// src/lib/line-item-order.ts
function makeRoomItemComparator(tuples) {
  const roomHasManualOrder = tuples.some((tuple) => tuple.lineItems?.some((li) => li.movedByUser === true));
  return (a, b) => {
    if (a.tuple.isLikely && b.tuple.isLikely) {
      return a.currentItem.lineItemId.localeCompare(b.currentItem.lineItemId);
    }
    if (a.tuple.isLikely && !b.tuple.isLikely) {
      return -1;
    }
    if (!a.tuple.isLikely && b.tuple.isLikely) {
      return 1;
    }
    if (roomHasManualOrder) {
      const aIdx = a.currentItem.orderIndex ?? Number.MAX_SAFE_INTEGER;
      const bIdx = b.currentItem.orderIndex ?? Number.MAX_SAFE_INTEGER;
      return aIdx - bIdx;
    }
    const aOrderIdx = a.tuple.lineItems.find((lineItem) => !lineItem.isComplement)?.orderIndex;
    const bOrderIdx = b.tuple.lineItems.find((lineItem) => !lineItem.isComplement)?.orderIndex;
    if (aOrderIdx === void 0 && bOrderIdx === void 0) {
      return 0;
    }
    if (aOrderIdx === void 0) {
      return 1;
    }
    if (bOrderIdx === void 0) {
      return -1;
    }
    const idxOrder = aOrderIdx - bOrderIdx;
    if (idxOrder !== 0) return idxOrder;
    if (a.tuple.tupleId === b.tuple.tupleId) {
      const aTuplePosition = a.tuple.lineItems?.findIndex((lineItem) => lineItem.lineItemId === a.currentItem.lineItemId);
      const bTuplePosition = b.tuple.lineItems?.findIndex((lineItem) => lineItem.lineItemId === b.currentItem.lineItemId);
      return (aTuplePosition || 0) - (bTuplePosition || 0);
    }
    const aFirstLineItem = a.tuple.lineItems[0]?.lineItem || "";
    const bFirstLineItem = b.tuple.lineItems[0]?.lineItem || "";
    if (aFirstLineItem.includes("remove") && bFirstLineItem.includes("replace")) {
      return -1;
    }
    if (bFirstLineItem.includes("remove") && aFirstLineItem.includes("replace")) {
      return 1;
    }
    return 0;
  };
}

// src/lib/filters.ts
var IN_PROGRESS_STATUSES = [
  "Received" /* RECEIVED */,
  "In progress" /* IN_PROGRESS */,
  "Waiting on information" /* WAITING_ON_INFORMATION */,
  "Estimating (Initial)" /* ESTIMATING_INITIAL */,
  "New" /* NEW */,
  "Sales" /* SALES */,
  "Planning" /* PLANNING */,
  "Estimating (Final)" /* ESTIMATING_FINAL */
];
var CLOSED_STATUSES = [
  "Under review" /* UNDER_REVIEW */,
  "Final review" /* FINAL_REVIEW */,
  "Approved" /* APPROVED */,
  "Paid" /* PAID */,
  "Closed" /* CLOSED */,
  "Invoicing" /* INVOICING */,
  "Receivables" /* RECEIVABLES */
];

// src/lib/feature-flags.ts
var FeatureFlagsManager = class {
  constructor() {
    this.features = {};
  }
  /**
   * Set features from backend API response
   */
  setFeatures(features) {
    this.features = { ...features };
  }
  /**
   * Clear all cached features
   */
  clearFeatures() {
    this.features = {};
  }
  /**
   * Check if a feature is enabled
   *
   * @param key - Feature key (e.g., "SHOW_DOCUSKETCH_UPLOAD")
   * @param organizationId - Current user's organization ID (optional, used for env fallback)
   * @param envGetter - Function to get environment variables (optional, used for fallback)
   * @param envPrefix - Environment variable prefix ("VITE_" or "EXPO_PUBLIC_")
   * @returns true if feature is enabled, false otherwise
   */
  isFeatureEnabled(key, organizationId, envGetter, envPrefix = "VITE_") {
    if (key in this.features) {
      return this.features[key];
    }
    if (envGetter && organizationId) {
      const envVarName = `${envPrefix}${key}`;
      const envValue = envGetter(envVarName);
      if (envValue) {
        try {
          const config = JSON.parse(envValue);
          if (config.organizationIds && Array.isArray(config.organizationIds)) {
            return config.organizationIds.includes(organizationId);
          }
        } catch (error) {
          console.warn(`Failed to parse environment variable ${envVarName}:`, error);
        }
      }
    }
    return false;
  }
  /**
   * Get all features
   */
  getAllFeatures() {
    return { ...this.features };
  }
};

// src/lib/space-path.ts
var GENERAL_SPACE_SEGMENT = "General";
function getSpaceNameChain(spaces, spaceId) {
  const byId = new Map(spaces.map((space) => [space.id, space]));
  const names = [];
  const visited = /* @__PURE__ */ new Set();
  let current = byId.get(spaceId);
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    names.unshift(current.name);
    current = current.parentId ? byId.get(current.parentId) : void 0;
  }
  return names;
}
function resolvePagePath({
  startPageId,
  getPage,
  spaces
}) {
  if (!startPageId) return [GENERAL_SPACE_SEGMENT];
  const pageTitles = [];
  const visited = /* @__PURE__ */ new Set();
  let head = [];
  let pageId = startPageId;
  while (pageId && !visited.has(pageId)) {
    visited.add(pageId);
    const page = getPage(pageId);
    if (!page) break;
    if (page.spaceId) {
      const chain = getSpaceNameChain(spaces, page.spaceId);
      head = chain.length ? chain : [page.title];
      break;
    }
    pageTitles.unshift(page.title);
    pageId = page.parentId;
  }
  return [...head, ...pageTitles].map((segment) => segment.trim()).filter(Boolean);
}
function isSameRoomText(left, right) {
  const normalize = (value) => (value ?? "").replace(/\s+/g, " ").trim().toLowerCase();
  return normalize(left) === normalize(right);
}

// src/constants/spaces.ts
var GENERAL_SPACE_ID = "general";
var MAX_SPACE_DEPTH = 3;

// src/utils/space-tree.ts
function buildSpaceHierarchy(spaces) {
  const presentIds = new Set(spaces.map((space) => space.id));
  const roots = [];
  const childrenByParentId = /* @__PURE__ */ new Map();
  for (const space of [...spaces].sort((a, b) => a.createdAt - b.createdAt)) {
    if (space.parentId && space.parentId !== space.id && presentIds.has(space.parentId)) {
      const siblings = childrenByParentId.get(space.parentId) ?? [];
      siblings.push(space);
      childrenByParentId.set(space.parentId, siblings);
    } else {
      roots.push(space);
    }
  }
  return { roots, childrenByParentId };
}
function orderSpaceIdsDepthFirst(spaces) {
  const { roots, childrenByParentId } = buildSpaceHierarchy(spaces);
  const ordered = [];
  const visited = /* @__PURE__ */ new Set();
  const visit = (space) => {
    if (visited.has(space.id)) return;
    visited.add(space.id);
    ordered.push(space.id);
    for (const child of childrenByParentId.get(space.id) ?? []) visit(child);
  };
  roots.forEach(visit);
  [...spaces].sort((a, b) => a.createdAt - b.createdAt).forEach(visit);
  return ordered;
}

// src/utils/xactimate-validation.ts
function parseDateString(dateStr) {
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date;
  }
  const parts = dateStr.split("/");
  if (parts.length === 3) {
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
      const parsed = new Date(year, month - 1, day);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
  }
  return null;
}
function getXactimateExportMissingFields(input) {
  const missing = [];
  const addressComponents = input.propertyAddressComponents ? JSON.parse(input.propertyAddressComponents) : [];
  if (!addressComponents.some((c) => c.types.includes("postal_code"))) {
    missing.push("address");
  }
  if (!input.dateOfLoss) missing.push("dateOfLoss");
  if (!input.dateContacted) missing.push("dateContacted");
  if (input.dateOfLoss && input.dateContacted) {
    const dol = parseDateString(input.dateOfLoss);
    const dc = parseDateString(input.dateContacted);
    if (dol && dc && dol.getTime() > dc.getTime()) {
      if (!missing.includes("dateOfLoss")) missing.push("dateOfLoss");
      if (!missing.includes("dateContacted")) missing.push("dateContacted");
    }
  }
  return missing;
}

// src/utils/photo-file-names.ts
var XACTIMATE_PHOTO_FILE_NAME_MAX_LENGTH = 35;
var XACTIMATE_PHOTO_NAME_MAX_TEXT_LENGTH = XACTIMATE_PHOTO_FILE_NAME_MAX_LENGTH - "-NNN".length - ".jpeg".length;
var DEFAULT_MAX_TEXT_LENGTH = 200;
function buildRoomNameByPageId(rooms) {
  return new Map(
    rooms.flatMap(
      (room) => room.metadata?.pageId && room.metadata.roomName ? [[room.metadata.pageId, room.metadata.roomName]] : []
    )
  );
}
function assignPhotoFileNames(photos, roomNameByPageId, { maxTextLength = DEFAULT_MAX_TEXT_LENGTH } = {}) {
  const counts = /* @__PURE__ */ new Map();
  return photos.map((photo) => {
    const estimateRoom = photo.scopeNoteRoomId ? roomNameByPageId?.get(photo.scopeNoteRoomId) : void 0;
    const room = estimateRoom ?? photo.roomPath?.join("");
    const base = (room ?? "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, maxTextLength) || "Photo";
    const n = (counts.get(base) ?? 0) + 1;
    counts.set(base, n);
    const labels = trimSegment(labelsSegment(photo.labels), maxTextLength - base.length - 1);
    return [base, labels, String(n).padStart(2, "0")].filter(Boolean).join("-");
  });
}
function labelsSegment(rawLabels) {
  return normalizePhotoLabels(rawLabels).flatMap((label) => label.name.split(/[^A-Za-z0-9_]+/)).filter(Boolean).join("-");
}
function trimSegment(segment, maxLength) {
  return maxLength > 0 ? segment.slice(0, maxLength).replace(/-+$/, "") : "";
}

// src/utils/tupleToLineItemConverter.ts
import { uuidv4 } from "zod";
var getTuplesFromRoom = (room) => Object.entries(room).filter(([key, value]) => key !== "metadata" && "tupleId" in value).map(([_, tuple]) => ({ ...tuple, pageMetadata: room.metadata }));
var tuplesToMappedLineItems = (tuples) => tuples.flatMap(
  (tuple) => tuple.lineItems.map((lineItem, idx) => ({
    uniqueId: lineItem.uniqueId ?? uuidv4(),
    lineItem,
    tuple: {
      ...tuple,
      lineItems: void 0
    }
  }))
);
function mappedLineItemsToTuples(mappedLineItems) {
  const tupleIdToTuple = {};
  for (const { tuple, lineItem, uniqueId } of mappedLineItems) {
    tupleIdToTuple[tuple.tupleId] ??= {
      ...tuple,
      lineItems: []
    };
    tupleIdToTuple[tuple.tupleId].lineItems.push({ ...lineItem, uniqueId });
  }
  const tuples = Object.values(tupleIdToTuple);
  tuples.forEach(
    (tuple) => tuple.lineItems.sort((lineItemA, lineItemB) => {
      if (lineItemA.isComplement) {
        return 1;
      }
      if (lineItemB.isComplement) {
        return -1;
      }
      return 0;
    })
  );
  return tuples;
}
var mappedLineItemsToRooms = (mappedLineItems) => {
  const roomMap = /* @__PURE__ */ new Map();
  for (const { tuple, lineItem } of mappedLineItems) {
    const normalizedRoomName = normalizeString2(tuple.pageMetadata.roomName);
    if (!roomMap.has(normalizedRoomName)) {
      roomMap.set(normalizedRoomName, {
        metadata: tuple.pageMetadata
      });
    }
    const room = roomMap.get(normalizedRoomName);
    if (!room[tuple.tupleId]) {
      room[tuple.tupleId] = { ...tuple, lineItems: [] };
    }
    room[tuple.tupleId].lineItems.push(lineItem);
  }
  return Array.from(roomMap.values());
};
var roomsToMappedLineItems = (rooms) => {
  const tuples = rooms.flatMap((room) => getTuplesFromRoom(room));
  return tuplesToMappedLineItems(tuples);
};
var perRoomFinalEstimateToEstimateDataForUI = (rooms) => {
  return rooms.map((room) => {
    const metadata = room.metadata;
    const tuplesOutput = [];
    for (const [key, value] of Object.entries(room)) {
      if (key === "metadata") continue;
      const tuple = value;
      if (!tuple.tupleId) {
        tuple.tupleId = key;
      }
      tuplesOutput.push(tuple);
    }
    return {
      dataForUI: {
        itemForDebugOnly: null,
        metadata,
        page: {
          id: metadata.pageId,
          ...metadata.mergeWith ? { mergeWith: metadata.mergeWith } : {},
          fullPageImagePath: metadata.fullPageImagePath,
          tuplesOutput
        },
        originalName: metadata.originalName,
        roomName: metadata.roomName
      }
    };
  });
};
var estimateDataForUIToPerRoomFinalEstimate = (estimateDataForUIs) => {
  return estimateDataForUIs.map((estimateDataForUI) => {
    const { dataForUI } = estimateDataForUI;
    const metadata = dataForUI.metadata;
    const room = {
      metadata
    };
    for (const tuple of dataForUI.page.tuplesOutput) {
      room[tuple.tupleId] = tuple;
    }
    return room;
  });
};

// src/utils/audit-trail-utils.ts
var setFieldByPath = (lineItem, path, value) => {
  if (path === "") return lineItem;
  const segments = path.split(".");
  let target = lineItem;
  for (let i = 0; i < segments.length - 1; i++) {
    target = target[segments[i]];
    if (target === void 0) return lineItem;
  }
  if (value === void 0) {
    delete target[segments[segments.length - 1]];
  } else {
    target[segments[segments.length - 1]] = value;
  }
  return lineItem;
};
var setOrDelete = (obj, key, value) => {
  if (value === void 0) {
    delete obj[key];
  } else {
    obj[key] = value;
  }
};
var reverseAuditTrail = (lineItem) => {
  const result = structuredClone(lineItem);
  const auditTrail = result.lineItem.auditTrail;
  if (!auditTrail?.length) return result;
  const sortedDiffs = [...auditTrail].sort((a, b) => b.dateTime - a.dateTime);
  for (const entry of sortedDiffs) {
    const { path, oldValue } = entry.diff;
    switch (path) {
      case "lineItem.lineItem" /* Description */:
        result.lineItem.lineItem = oldValue ?? result.lineItem.lineItem;
        break;
      case "lineItem.lineItemId" /* LineItemId */:
        result.lineItem.lineItemId = oldValue ?? result.lineItem.lineItemId;
        break;
      case "lineItem.formula.rawFormula" /* Formula */:
        if (result.lineItem.formula) {
          result.lineItem.formula.rawFormula = oldValue ?? result.lineItem.formula.rawFormula;
        }
        break;
      case "tuple.hasError" /* HasError */:
        result.tuple.hasError = oldValue === true || oldValue === "true";
        break;
      case "lineItem.issueExplanation" /* IssueExplanation */:
        setOrDelete(result.lineItem, "issueExplanation", oldValue);
        break;
      case "lineItem.confidence" /* Confidence */:
        setOrDelete(result.lineItem, "confidence", oldValue);
        break;
      case "lineItem.confirmed" /* Confirmed */:
        setOrDelete(result.lineItem, "confirmed", oldValue);
        break;
      case "lineItem.comment" /* Comment */:
        setOrDelete(result.lineItem, "comment", oldValue);
        break;
      case "lineItem.archived" /* Archived */:
        setOrDelete(result.lineItem, "archived", oldValue);
        break;
      case "tuple.pageMetadata.roomName" /* RoomName */:
        if (result.tuple.pageMetadata) {
          result.tuple.pageMetadata.roomName = oldValue ?? result.tuple.pageMetadata.roomName;
        }
        break;
      case "lineItem.addedByTPARule" /* AddedByTPARule */:
        setOrDelete(result.lineItem, "addedByTPARule", oldValue);
        break;
      case "lineItem.removedByTPARule" /* RemovedByTPARule */:
        setOrDelete(result.lineItem, "removedByTPARule", oldValue);
        break;
      case "tuple.isLikely" /* IsLikely */:
        setOrDelete(result.tuple, "isLikely", oldValue);
        break;
      case "lineItem.isComplement" /* IsComplement */:
        setOrDelete(result.lineItem, "isComplement", oldValue);
        break;
      case "lineItem.isMutuallyExcluded" /* IsMutuallyExcluded */:
        setOrDelete(result.lineItem, "isMutuallyExcluded", oldValue);
        break;
    }
  }
  return result;
};
var hasId = (entry) => entry.id !== void 0;
var ensureAuditTrailIds = (auditTrail) => auditTrail.map((entry) => hasId(entry) ? entry : { ...entry, id: crypto.randomUUID() });
var copyAuditTrail = (auditTrail) => {
  const copyIdById = /* @__PURE__ */ new Map();
  const copies = (auditTrail ?? []).map((entry) => {
    const id = crypto.randomUUID();
    if (entry.id !== void 0) copyIdById.set(entry.id, id);
    return { ...structuredClone(entry), id };
  });
  return copies.map(
    (entry) => entry.source === "reversal" && copyIdById.has(entry.revertedDiffId) ? { ...entry, revertedDiffId: copyIdById.get(entry.revertedDiffId) } : entry
  );
};
var getActiveAuditTrail = (auditTrail) => {
  const revertedDiffIds = /* @__PURE__ */ new Set();
  for (const entry of auditTrail ?? []) {
    if (entry.source === "reversal") revertedDiffIds.add(entry.revertedDiffId);
  }
  return (auditTrail ?? []).filter((entry) => entry.source !== "reversal" && (entry.id === void 0 || !revertedDiffIds.has(entry.id)));
};
var MAX_AUDIT_TRAIL_LENGTH = 256;
var compactAuditTrail = (auditTrail) => auditTrail.length > MAX_AUDIT_TRAIL_LENGTH ? getActiveAuditTrail(auditTrail) : auditTrail;
var compactLineItemAuditTrail = (lineItem) => lineItem.auditTrail === void 0 ? lineItem : { ...lineItem, auditTrail: compactAuditTrail(lineItem.auditTrail) };
var compactTupleAuditTrails = (tuple) => ({
  ...tuple,
  lineItems: tuple.lineItems.map(compactLineItemAuditTrail)
});
var compactRoomAuditTrails = (room) => Object.fromEntries(
  Object.entries(room).map(([key, value]) => [
    key,
    key !== "metadata" && "tupleId" in value ? compactTupleAuditTrails(value) : value
  ])
);
var INVERSE_DIFF_TYPE = {
  added: "removed",
  removed: "added",
  modified: "modified",
  "type-changed": "type-changed"
};
var createReversalDiff = (entry, dateTime, triggeredBy) => {
  const diff = { path: entry.diff.path, type: INVERSE_DIFF_TYPE[entry.diff.type] };
  if (entry.diff.newValue !== void 0) diff.oldValue = entry.diff.newValue;
  if (entry.diff.oldValue !== void 0) diff.newValue = entry.diff.oldValue;
  return { id: crypto.randomUUID(), source: "reversal", revertedDiffId: entry.id, triggeredBy, diff, dateTime };
};
var createCreationReversalDiff = (entry, wasArchived, dateTime, triggeredBy) => ({
  id: crypto.randomUUID(),
  source: "reversal",
  revertedDiffId: entry.id,
  triggeredBy,
  diff: wasArchived === void 0 ? { path: "lineItem.archived" /* Archived */, type: "added", newValue: true } : { path: "lineItem.archived" /* Archived */, type: "modified", oldValue: wasArchived, newValue: true },
  dateTime
});
var revertAllDiffsAtAndAfter = (lineItem, isFirstDiffToRevert, triggeredBy) => {
  const auditTrail = ensureAuditTrailIds(lineItem.lineItem.auditTrail ?? []);
  const activeDiffs = getActiveAuditTrail(auditTrail).sort((a, b) => a.dateTime - b.dateTime);
  const matchIndex = activeDiffs.findIndex(isFirstDiffToRevert);
  if (matchIndex === -1) return lineItem;
  const result = structuredClone(lineItem);
  result.lineItem.auditTrail = structuredClone(auditTrail);
  const diffsToRevert = structuredClone(activeDiffs.slice(matchIndex)).reverse();
  const dateTime = Date.now();
  const reversalDiffs = [];
  for (const entry of diffsToRevert) {
    if (entry.diff.path === "" /* LineItemCreated */) {
      reversalDiffs.push(createCreationReversalDiff(entry, result.lineItem.archived, dateTime, triggeredBy));
      result.lineItem.archived = true;
      continue;
    }
    setFieldByPath(result, entry.diff.path, entry.diff.type === "added" ? void 0 : entry.diff.oldValue);
    reversalDiffs.push(createReversalDiff(entry, dateTime, triggeredBy));
  }
  result.lineItem.auditTrail = [...result.lineItem.auditTrail, ...reversalDiffs];
  return result;
};
var isCreationReverted = (lineItem) => {
  const auditTrail = lineItem.lineItem.auditTrail ?? [];
  const activeDiffs = new Set(getActiveAuditTrail(auditTrail));
  return auditTrail.some(
    (entry) => entry.diff.path === "" /* LineItemCreated */ && entry.source !== "reversal" && !activeDiffs.has(entry)
  );
};
var isArchivedByCreationReversal = (lineItem) => {
  const auditTrail = lineItem.lineItem.auditTrail ?? [];
  const mostRecent = auditTrail.reduce(
    (latest, entry) => latest === void 0 || entry.dateTime >= latest.dateTime ? entry : latest,
    void 0
  );
  if (mostRecent?.source !== "reversal") return false;
  return auditTrail.some((entry) => entry.id === mostRecent.revertedDiffId && entry.diff.path === "" /* LineItemCreated */);
};
var findFieldModifiedBy = (lineItem, diffPath) => getActiveAuditTrail(lineItem.lineItem.auditTrail).find((diff) => diff.diff.type === "modified" && diff.diff.path === diffPath)?.source;
var revertAllDiffsAtAndAfterForList = (lineItems, isFirstDiffToRevert, triggeredBy) => {
  const result = [];
  for (const lineItem of lineItems) {
    if (lineItem.lineItem.auditTrail === void 0) {
      console.warn(
        `Line item "${lineItem.lineItem.lineItem}" in room ${lineItem.tuple.pageMetadata?.roomName} doesn't have an audit trail, will keep the same`
      );
      result.push(lineItem);
      continue;
    }
    result.push(revertAllDiffsAtAndAfter(lineItem, isFirstDiffToRevert, triggeredBy));
  }
  return result;
};

// src/utils/estimate-creation-source.ts
var ESTIMATE_CREATION_SOURCE_DISPLAY_NAMES = {
  ["REBUILD" /* REBUILD */]: "Rebuild",
  ["REBUILD_WEB" /* REBUILD_WEB */]: "Web App",
  ["REBUILD_MOBILE" /* REBUILD_MOBILE */]: "Mobile App",
  ["XCELERATE" /* XCELERATE */]: "Xcelerate",
  ["GOOGLE_FORM" /* GOOGLE_FORM */]: "Google Form",
  ["JOB_NIMBUS" /* JOB_NIMBUS */]: "JobNimbus",
  ["FIRST_ATLANTIC" /* FIRST_ATLANTIC */]: "PSA",
  ["ALBI" /* ALBI */]: "Albi",
  ["PSA" /* PSA */]: "PSA"
};
function isCrmEstimateCreationSource(creationSource) {
  return !!creationSource && CRM_ESTIMATE_CREATION_SOURCES.includes(creationSource);
}
function getEstimateCreationSourceDisplayName(creationSource) {
  if (!creationSource) return void 0;
  return ESTIMATE_CREATION_SOURCE_DISPLAY_NAMES[creationSource] ?? creationSource;
}
function getCrmCreationSourceDisplayName(creationSource) {
  if (!isCrmEstimateCreationSource(creationSource)) return void 0;
  return getEstimateCreationSourceDisplayName(creationSource);
}

// src/utils/slack.ts
var postSlackMessage = async (channelId, botToken, payload) => {
  if (!botToken || !channelId) {
    throw new Error("bot token or channel id is not configured");
  }
  const response = await fetch("https://slack.com/api/chat.postMessage", {
    method: "POST",
    headers: {
      "Content-Type": "application/json; charset=utf-8",
      Authorization: `Bearer ${botToken}`
    },
    body: JSON.stringify({
      channel: channelId,
      ...payload
    })
  });
  const result = await response.json();
  if (!result.ok) {
    throw new Error(`Slack API error: ${result.error}`);
  }
  return result.ts;
};

// src/utils/measurement/dimensions.ts
var dimensionsToMeasurements = ({
  length,
  width,
  ceilingHeight
}) => {
  const r1 = (n) => Math.round(n * 10) / 10;
  const area = r1(length * width);
  const perimeter = r1(2 * (length + width));
  const result = {
    ["f" /* Floors */]: area,
    ["c" /* Ceilings */]: area,
    ["pf" /* FloorPerimeter */]: perimeter,
    ["pc" /* CeilingPerimeter */]: perimeter
  };
  if (ceilingHeight && ceilingHeight > 0) {
    result["w" /* Walls */] = r1(perimeter * ceilingHeight);
  }
  return result;
};

// src/utils/undefined-field-utils.ts
var isPlainObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value) && Object.getPrototypeOf(value) === Object.prototype;
var stripUndefinedFields = (input) => {
  const undefinedPaths = [];
  const walk = (value, path) => {
    if (Array.isArray(value)) {
      return value.map((item, i) => {
        const itemPath = `${path}[${i}]`;
        if (item === void 0) {
          undefinedPaths.push(itemPath);
          return void 0;
        }
        return walk(item, itemPath);
      }).filter((item) => item !== void 0);
    }
    if (isPlainObject(value)) {
      const result = {};
      for (const key of Object.keys(value)) {
        const childPath = path ? `${path}.${key}` : key;
        const childValue = value[key];
        if (childValue === void 0) {
          undefinedPaths.push(childPath);
          continue;
        }
        result[key] = walk(childValue, childPath);
      }
      return result;
    }
    return value;
  };
  const cleaned = walk(input, "");
  return { cleaned, undefinedPaths };
};

// src/utils/pdf-preview/currency.ts
var parseCurrencyString = (value) => {
  return parseFloat(value.replace(/,/g, "")) || 0;
};
var formatCurrencyToString = (value) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};
var formatPrice = (price) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price);
};
var formatCurrencyInput = (value) => {
  const cleanedValue = value.replace(/[^\d.]/g, "");
  if (cleanedValue === "") {
    return "";
  }
  const [rawIntegerPart, rawDecimalPart] = cleanedValue.split(".");
  const trimmedIntegerPart = rawIntegerPart.replace(/^0+(?=\d)/, "");
  const integerPart = trimmedIntegerPart === "" ? "0" : trimmedIntegerPart;
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (rawDecimalPart !== void 0) {
    return `${formattedInteger}.${rawDecimalPart.slice(0, 2)}`;
  }
  return formattedInteger;
};
var formatPercentInput = (value) => {
  const cleanedValue = value.replace(/[^\d.]/g, "");
  if (cleanedValue === "") {
    return "";
  }
  const [rawIntegerPart, rawDecimalPart] = cleanedValue.split(".");
  const trimmedIntegerPart = rawIntegerPart.replace(/^0+(?=\d)/, "");
  const integerPart = trimmedIntegerPart === "" ? "0" : trimmedIntegerPart;
  const formatted = rawDecimalPart !== void 0 ? `${integerPart}.${rawDecimalPart.slice(0, 2)}` : integerPart;
  return parseFloat(formatted) > 100 ? "100" : formatted;
};

// src/utils/pdf-preview/pricing.ts
var parseTaxRateInput = (value) => {
  return parseCurrencyString(value) / 100;
};
var formatTaxRateInput = (taxRate, fractionDigits = 2) => {
  if (taxRate === void 0) {
    return "";
  }
  return (taxRate * 100).toFixed(fractionDigits);
};
var calculateOverheadAndProfit = (subtotal, overheadPercent = 10, profitPercent = 10) => {
  const overheadRate = overheadPercent / 100;
  const profitRate = profitPercent / 100;
  return {
    overhead: subtotal * overheadRate,
    profit: subtotal * profitRate
  };
};
var calculateTaxRate = (previewData) => {
  if (previewData?.taxRate !== void 0 && Number.isFinite(previewData.taxRate)) {
    return previewData.taxRate;
  }
  if (!previewData?.taxAmount || !previewData?.preTaxTotal) {
    return 0;
  }
  const originalSubtotal = parseCurrencyString(previewData.preTaxTotal);
  const originalOverhead = parseCurrencyString(previewData.overhead || "0");
  const originalProfit = parseCurrencyString(previewData.profit || "0");
  const originalSubtotalWithMarkup = originalSubtotal + originalOverhead + originalProfit;
  const originalTax = parseCurrencyString(previewData.taxAmount);
  if (originalSubtotalWithMarkup <= 0) {
    return 0;
  }
  return originalTax / originalSubtotalWithMarkup;
};

// src/utils/pdf-preview/pdfPreview.ts
var DEFAULT_PDF_OVERHEAD_PROFIT_PERCENT = 10;
function transformScopeOfWorkToString(scopeOfWork) {
  if (scopeOfWork.type === "category-grouped") {
    return scopeOfWork.scopeOfWorkCategories.map((category) => `${category.category}
	${category.summary}`).join("\n\n");
  }
  return scopeOfWork.scopeOfWorkRooms.map((room) => {
    const categoriesText = room.categories.map((category) => {
      const itemsText = category.items.map((item) => `		- ${item.item}`).join("\n");
      return `	${category.category}
${itemsText}`;
    }).join("\n");
    return `${room.room}
${categoriesText}`;
  }).join("\n\n");
}
function extractScopeText(scopeData) {
  if (scopeData.type === "category-grouped" && scopeData.textOverride) {
    return scopeData.textOverride;
  }
  return transformScopeOfWorkToString(scopeData);
}
function extractInitialSubtotal(previewData) {
  if (previewData.subtotal) {
    return formatCurrencyInput(previewData.subtotal);
  }
  return "";
}
function hasBreakdownData(data) {
  return data?.overhead !== void 0 && data?.profit !== void 0;
}
function getDefaultIncludeOverheadAndProfit(estimateType, previewData) {
  if (!previewData || !hasBreakdownData(previewData)) {
    return false;
  }
  const overheadPercent = previewData.overheadPercent ?? DEFAULT_PDF_OVERHEAD_PROFIT_PERCENT;
  const profitPercent = previewData.profitPercent ?? DEFAULT_PDF_OVERHEAD_PROFIT_PERCENT;
  if (estimateType === "RECONSTRUCTION" /* RECONSTRUCTION */) {
    return true;
  }
  if (overheadPercent !== DEFAULT_PDF_OVERHEAD_PROFIT_PERCENT || profitPercent !== DEFAULT_PDF_OVERHEAD_PROFIT_PERCENT) {
    return true;
  }
  return false;
}
function isRoomGrouped(previewData) {
  return previewData?.scopeOfWorkCategories?.type === "room-grouped";
}
function calculatePricing(subtotal, previewData, includeOverheadAndProfit, includeTax = Boolean(previewData?.taxAmount)) {
  const subtotalNum = parseCurrencyString(subtotal);
  const hasBreakdown = hasBreakdownData(previewData);
  const overheadPercent = previewData?.overheadPercent ?? DEFAULT_PDF_OVERHEAD_PROFIT_PERCENT;
  const profitPercent = previewData?.profitPercent ?? DEFAULT_PDF_OVERHEAD_PROFIT_PERCENT;
  let overheadValue = 0;
  let profitValue = 0;
  if (hasBreakdown && includeOverheadAndProfit) {
    const result = calculateOverheadAndProfit(subtotalNum, overheadPercent, profitPercent);
    overheadValue = result.overhead;
    profitValue = result.profit;
  }
  const overheadFormatted = formatCurrencyToString(overheadValue);
  const profitFormatted = formatCurrencyToString(profitValue);
  const baseForTax = hasBreakdown ? subtotalNum + overheadValue + profitValue : subtotalNum;
  const taxRate = includeTax ? calculateTaxRate(previewData) : 0;
  const taxAmount = formatCurrencyToString(baseForTax * taxRate);
  const taxNum = parseCurrencyString(taxAmount);
  const baseForTotal = hasBreakdown ? subtotalNum + overheadValue + profitValue : subtotalNum;
  const total = formatCurrencyToString(baseForTotal + taxNum);
  const preTaxTotal = hasBreakdown && includeOverheadAndProfit ? formatCurrencyToString(subtotalNum + overheadValue + profitValue) : formatCurrencyToString(subtotalNum);
  return {
    overhead: overheadValue,
    profit: profitValue,
    overheadFormatted,
    profitFormatted,
    taxAmount,
    total,
    preTaxTotal,
    overheadPercent,
    profitPercent
  };
}
function cleanCurrencyForApi(value) {
  return formatCurrencyToString(parseCurrencyString(value));
}
function buildPricingAndDimensionInfo(isTimeAndMaterial, pricingData, dimensionsData) {
  return isTimeAndMaterial ? { pricing: [], dimension: dimensionsData, tmPricing: pricingData } : { pricing: pricingData, dimension: dimensionsData };
}

// src/utils/langfuse/uploadDraftLineItemDatasetItem.ts
import { LangfuseClient } from "@langfuse/client";
var DRAFT_LINE_ITEMS_DATASET_NAME = "draft-line-items";
var ensureDatasetExists = async (langfuseClient, datasetName) => {
  try {
    await langfuseClient.api.datasets.get(datasetName);
  } catch {
    await langfuseClient.api.datasets.create({ name: datasetName });
  }
};
var uploadDraftLineItemDatasetItem = async (args) => {
  const {
    langfuseConfig,
    datasetName = DRAFT_LINE_ITEMS_DATASET_NAME,
    scopeNoteText,
    sourceText,
    roomName,
    lineItemId,
    lineItemStr,
    formulaRaw,
    estimateId,
    estimateType,
    source,
    notes
  } = args;
  const langfuseClient = new LangfuseClient({
    secretKey: langfuseConfig.secretKey,
    publicKey: langfuseConfig.publicKey,
    baseUrl: langfuseConfig.baseUrl ?? "https://cloud.langfuse.com"
  });
  const input = { scopeNoteText, sourceText, roomName };
  const expectedOutput = {
    lineItems: [
      {
        lineItemId,
        lineItemStr,
        formulaRaw
      }
    ]
  };
  const metadata = {
    estimateId,
    estimateType,
    source,
    ...notes !== void 0 ? { notes } : {}
  };
  await ensureDatasetExists(langfuseClient, datasetName);
  const result = await langfuseClient.api.datasetItems.create({
    datasetName,
    input,
    expectedOutput,
    metadata
  });
  return { id: result.id, input, expectedOutput, metadata };
};

// src/utils/instatus-status.ts
var ACTIVE_INCIDENT_STATUSES = /* @__PURE__ */ new Set(["INVESTIGATING", "IDENTIFIED", "MONITORING"]);
var ACTIVE_MAINTENANCE_STATUSES = /* @__PURE__ */ new Set(["NOTSTARTEDYET", "INPROGRESS"]);
var DEFAULT_STATUS_MESSAGE = "Some features may be unavailable. Please try again soon.";
function normalizeStatus(status) {
  return status?.trim().toUpperCase() ?? "";
}
function getActiveIncident(summary) {
  return summary.activeIncidents?.find((incident) => ACTIVE_INCIDENT_STATUSES.has(normalizeStatus(incident.status)));
}
function getActiveMaintenance(summary) {
  return summary.activeMaintenances?.find((maintenance) => ACTIVE_MAINTENANCE_STATUSES.has(normalizeStatus(maintenance.status)));
}
function getInstatusOutageBannerState(summary) {
  const activeIncident = getActiveIncident(summary);
  if (activeIncident) {
    return {
      visible: true,
      kind: "incident",
      title: "Service issue",
      message: DEFAULT_STATUS_MESSAGE,
      href: activeIncident.url ?? summary.page.url
    };
  }
  const activeMaintenance = getActiveMaintenance(summary);
  if (activeMaintenance) {
    return {
      visible: true,
      kind: "maintenance",
      title: "Scheduled maintenance",
      message: DEFAULT_STATUS_MESSAGE,
      href: activeMaintenance.url ?? summary.page.url
    };
  }
  return { visible: false };
}

// src/utils/mitigation-cat-utils.ts
function isContaminationCat(cat) {
  return cat === 2 || cat === 3;
}
function catChangeRequiresLineItemResync(prevCat, nextCat) {
  return prevCat !== nextCat && (isContaminationCat(prevCat) || isContaminationCat(nextCat));
}

// src/utils/rules-engine/rule-query-fields.ts
var RULE_TEMPLATES = [
  ADD_F9_NOTE,
  BULK_TRANSFORM_LINE_ITEMS,
  COMPLEMENTS_AND_RECOMMENDATIONS,
  DEFAULT_QUANTITIES,
  GENERATE_FROM_ESTIMATE_FIELD,
  INHERIT_FORMULA,
  LINE_ITEM_TRANSFORMATION,
  MERGE_COMPLEMENTS,
  MULTI_PARENT_RECOMMENDATIONS,
  MULTI_PARENT_TRANSFORMATIONS,
  MUTUALLY_EXCLUSIVE,
  ONE_LINE_ITEM_UPDATES_ANOTHER,
  PAINT_AFFECTED_AND_CONTINUOUS_SURFACE,
  SPLIT_LINE_ITEMS,
  UPDATE_AFTER_HOURS
];
var UNSUPPORTED_FIELD_TYPES = /* @__PURE__ */ new Set(["range", "quantityFunction"]);
function collectFields(inputFields, parentPath, arrayPath) {
  const fields = [];
  for (const field of inputFields) {
    const path = parentPath ? `${parentPath}.${field.name}` : field.name;
    if (field.type === "object") {
      fields.push(...collectFields(field.properties, path, arrayPath));
      continue;
    }
    if (field.type === "array") {
      if (arrayPath !== null) continue;
      fields.push(...collectFields(field.items, "", path));
      continue;
    }
    if (field.type === "lineItem") {
      const lineItemField = { path, name: field.name, type: "lineItem" /* LineItem */ };
      if (field.description !== void 0) lineItemField.description = field.description;
      if (arrayPath !== null) lineItemField.arrayPath = arrayPath;
      fields.push(lineItemField);
      continue;
    }
    if (UNSUPPORTED_FIELD_TYPES.has(field.type)) continue;
    const collectedField = {
      path,
      name: field.name,
      // Safe: the UNSUPPORTED_FIELD_TYPES guard above already skipped the leaf types not in RuleQueryFieldType.
      type: field.type
    };
    if (field.description !== void 0) collectedField.description = field.description;
    if (field.type === "enum") {
      collectedField.values = field.values;
    }
    if (arrayPath !== null) collectedField.arrayPath = arrayPath;
    fields.push(collectedField);
  }
  return fields;
}
var RULE_QUERY_FIELDS = Object.fromEntries(
  RULE_TEMPLATES.map((template) => [
    template.id,
    [...collectFields(RULE_RUN_CRITERIA, "runCriteria", null), ...collectFields(template.inputField.properties, "ruleInputs", null)]
  ])
);

// src/utils/env-utils.ts
var requireEnvVar = (name, context) => {
  const value = process.env[name];
  if (!value) {
    throw new Error(context ? `${name} is not set (required by ${context})` : `${name} is not set`);
  }
  return value;
};

// src/utils/rules-engine/validate-rule-inputs-shape.ts
function validateRuleInputsShape(templateId, ruleInputs) {
  const template = RULE_TEMPLATES.find((t) => t.id === templateId);
  if (!template) {
    return [`Unknown templateId "${templateId}".`];
  }
  const errors = [];
  validateObject(ruleInputs, template.inputField.properties, "ruleInputs", errors);
  return errors;
}
function isPlainObject2(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function validateObject(value, properties, path, errors) {
  if (!isPlainObject2(value)) {
    errors.push(`${path} must be an object.`);
    return;
  }
  const declared = new Map(properties.map((p) => [p.name, p]));
  for (const key of Object.keys(value)) {
    if (!declared.has(key)) {
      errors.push(`${path}.${key} is not a valid field for this template.`);
    }
  }
  for (const [key, fieldValue] of Object.entries(value)) {
    const field = declared.get(key);
    if (field) {
      validateField(fieldValue, field, `${path}.${key}`, errors);
    }
  }
}
function validateField(value, field, path, errors) {
  switch (field.type) {
    case "object":
      validateObject(value, field.properties, path, errors);
      return;
    case "array": {
      if (!Array.isArray(value)) {
        errors.push(`${path} must be an array.`);
        return;
      }
      value.forEach((item, i) => validateObject(item, field.items, `${path}[${i}]`, errors));
      return;
    }
    case "string":
      if (typeof value !== "string") errors.push(`${path} must be a string.`);
      return;
    case "number":
      if (typeof value !== "number") errors.push(`${path} must be a number.`);
      return;
    case "boolean":
      if (typeof value !== "boolean") errors.push(`${path} must be a boolean.`);
      return;
    case "enum": {
      if (field.isMultiSelect) {
        if (!Array.isArray(value) || value.some((v) => typeof v !== "string")) {
          errors.push(`${path} must be an array of strings.`);
          return;
        }
        const bad = value.filter((v) => !field.values.includes(v));
        if (bad.length) errors.push(`${path} contains invalid value(s): ${bad.join(", ")}.`);
        return;
      }
      if (typeof value !== "string" || !field.values.includes(value)) {
        errors.push(`${path} must be one of: ${field.values.join(", ")}.`);
      }
      return;
    }
    case "lineItem":
      if (!isPlainObject2(value) || typeof value.lineItemId !== "string") {
        errors.push(`${path} must be a line item object with a string lineItemId.`);
      }
      return;
    case "catCodes":
      if (!Array.isArray(value) || value.some((v) => typeof v !== "string")) {
        errors.push(`${path} must be an array of strings.`);
      }
      return;
    case "organizations":
    case "tpas":
    case "carriers":
      if (!isPlainObject2(value) || !INCLUDE_OR_EXCLUDE_VALUES.includes(value.includeOrExclude) || !Array.isArray(value.ids) || value.ids.some((v) => typeof v !== "string")) {
        errors.push(`${path} must be an object with includeOrExclude (INCLUDE/EXCLUDE) and a string ids array.`);
      }
      return;
    // `range` and `quantityFunction` have loosely-specified value shapes in the descriptor, so we don't check them.
    default:
      return;
  }
}
export {
  ADD_F9_NOTE,
  AREA_SHORTHANDS,
  AlbiEstimateNameFormat,
  BULK_TRANSFORM_LINE_ITEMS,
  CLOSED_STATUSES,
  COMPLEMENTS_AND_RECOMMENDATIONS,
  CRMIntegrationType,
  CRM_ESTIMATE_CREATION_SOURCES,
  CRM_INTEGRATION_BASE_KEYS,
  CRM_INTEGRATION_FIELDS,
  CRM_INTEGRATION_SLUG_PATTERN,
  ConversationType,
  CustomPricing,
  DEFAULT_QUANTITIES,
  DEPLOY_ENVIRONMENTS,
  DEPLOY_PROVIDERS,
  DEPLOY_SERVICES,
  DRAFT_LINE_ITEMS_DATASET_NAME,
  DayOfWeek,
  EDrywallWidthCalculation,
  EExtraDataMappingType,
  EExtraVariableValues,
  ESTIMATE_CREATION_SOURCE_DISPLAY_NAMES,
  ESTIMATE_TYPE_LABELS,
  ESTIMATE_TYPE_TO_TAGS,
  ESkipProcessingReason,
  EstimateCreationSource,
  EstimateFieldsForExportHeader,
  EstimateReferenceNumberType,
  EstimateStatus,
  EstimateType,
  EstimaticsFileTypes,
  FEATURE_FLAGS,
  FINAL_MODULE_TEMPLATE_ID,
  FLOOD_CUT_LINE_ITEM_IDS,
  FORMULA_SHORTHAND_VALUES,
  FORMULA_UNIT_VALUES,
  FeatureFlagsManager,
  FileErrorDummyPath,
  FormatRawFormulaError,
  FormulaShorthand,
  FormulaUnit,
  GENERAL_SPACE_ID,
  GENERATE_FROM_ESTIMATE_FIELD,
  GeneralRoomOrder,
  Holiday,
  INCLUDE_OR_EXCLUDE_VALUES,
  INHERIT_FORMULA,
  IN_PROGRESS_STATUSES,
  ISSUE_CONFIDENCE_THRESHOLD,
  JobStatus,
  JobType,
  Kind,
  LINE_ITEM_TRANSFORMATION,
  LineItemCategory,
  LineItemDiffPath,
  MAX_AUDIT_TRAIL_LENGTH,
  MAX_PHOTOS_PER_DELETE,
  MAX_PHOTOS_PER_LABEL_APPLY,
  MAX_PHOTO_LABELS_PER_ORG,
  MAX_SPACE_DEPTH,
  MERGE_COMPLEMENTS,
  MULTI_PARENT_RECOMMENDATIONS,
  MULTI_PARENT_TRANSFORMATIONS,
  MUTUALLY_EXCLUSIVE,
  ManualEstimateReviewStage,
  ManualEstimateReviewUploadSource,
  MitToReconMappingAction,
  ModuleLevel,
  ModuleType,
  NOTE_TYPE_TO_ESTIMATE_TYPE,
  NOTE_TYPE_VALUES,
  NoteFlowType,
  NoteType,
  ONE_LINE_ITEM_UPDATES_ANOTHER,
  PAINT_AFFECTED_AND_CONTINUOUS_SURFACE,
  PCWidthCalculation,
  PERIMETER_SHORTHANDS,
  PFWidthCalculation,
  PHOTO_LABEL_NAME_MAX_LENGTH,
  PHOTO_REPORT_CAPTION_MAX_LENGTH,
  PROJECT_ROOM_ID,
  PdfFileFormat,
  QueuedItemToProcessStatus,
  RULE_QUERY_FIELDS,
  RULE_RUN_CRITERIA,
  RULE_TEMPLATES,
  RULE_TEMPLATE_IDS,
  ResponsiveNoteTemplateVisitType,
  RoomMeasurementKey,
  RoomType,
  RuleQueryFieldType,
  SHORTHAND_DESCRIPTION_FOR_LLM,
  SHORTHAND_TO_UNIT,
  SPLIT_LINE_ITEMS,
  ScopeNoteSource,
  ScopeTag,
  TPARuleTypes,
  TicSheetType,
  UPDATE_AFTER_HOURS,
  VERCEL_DEPLOY_SERVICES,
  VerboseSowPdfMode,
  XACTIMATE_CATEGORIES,
  XACTIMATE_PHOTO_FILE_NAME_MAX_LENGTH,
  XACTIMATE_PHOTO_NAME_MAX_TEXT_LENGTH,
  XCELERATE_STATUSES,
  addTwoFormulas,
  appendOrderKey,
  areValueDependenciesNotSatisfied,
  assignPhotoFileNames,
  attemptToFillInFormulaShorthands,
  attemptToStripUnits,
  breakUpFormulaIntoComponents,
  buildBlankReeseScopeContext,
  buildBlankReeseScopedFirstMessage,
  buildDisabledTooltip,
  buildPricingAndDimensionInfo,
  buildRoomNameByPageId,
  buildSpaceHierarchy,
  buildSystemPromptFromCustom,
  buildTemplatedReeseFirstMessage,
  buildTemplatedResponsiveNoteReesePrompt,
  calculateOverheadAndProfit,
  calculatePricing,
  calculateTaxRate,
  capitalizeWords,
  cascadeSelectionUpdate,
  catChangeRequiresLineItemResync,
  checkIfFormulaHasCorrectUnits,
  cleanCurrencyForApi,
  combineFormulasKeepingSeparate,
  compactAuditTrail,
  compactRoomAuditTrails,
  compactTupleAuditTrails,
  compareOrderKeys,
  compareShorthands,
  configureSharedAPI,
  containsCappableShorthand,
  convertFormulaToDifferentUnit,
  convertWallClockBetweenTimeZones,
  copyAuditTrail,
  dimensionsToMeasurements,
  divideFormula,
  ensureAuditTrailIds,
  estimateDataForUIToPerRoomFinalEstimate,
  extractInitialSubtotal,
  extractQuantityFromFormula,
  extractScopeText,
  extractUnitFromFormula,
  findFieldModifiedBy,
  formatCurrencyInput,
  formatCurrencyToString,
  formatPercentInput,
  formatPhotoLabelNames,
  formatPrice,
  formatTaxRateInput,
  generateOrderKeys,
  getActiveAuditTrail,
  getConversationInputParams,
  getCrmCreationSourceDisplayName,
  getCurrentEquipmentPerRoomContext,
  getDefaultActivity,
  getDefaultIncludeOverheadAndProfit,
  getDefaultSelectionsFromLineItem,
  getEstimateCreationSourceDisplayName,
  getEstimateTypeLabel,
  getFormulaErrorMessage,
  getGuardrailsInfo,
  getInstatusOutageBannerState,
  getLineItemState,
  getMatchedSelectionsFromLineItem,
  getMaterialRemovedFollowUpInfo,
  getMaximumFormula,
  getMostRecentEquipmentCountSummary,
  getNoteTypeFromEstimateType,
  getPersonalityInfo,
  getQueuedItemToProcessStatusName,
  getRawFormulaFromFormula,
  getSelectionsFromLineItemText,
  getSharedAuthHeaders,
  getSharedBaseUrl,
  getSpaceNameChain,
  getTimeZoneOffsetMinutes,
  getUtcMsForWallClock,
  getWallClockPartsInTimeZone,
  getXactimateExportMissingFields,
  hasBreakdownData,
  isArchivedByCreationReversal,
  isContaminationCat,
  isCreationReverted,
  isCrmEstimateCreationSource,
  isItemArchived,
  isLegacyEstimateJob,
  isLineItemExcluded,
  isNewFoundationScopeNoteJob,
  isNewFoundationSource,
  isPackageOrderKey,
  isRoomGrouped,
  isRoomNameDuplicate,
  isSameRoomText,
  makeRoomItemComparator,
  mappedLineItemsToRooms,
  mappedLineItemsToTuples,
  multiplyFormula,
  normalizePhotoLabels,
  normalizeString2 as normalizeString,
  orderKeyBetween,
  orderSpaceIdsDepthFirst,
  parseCurrencyString,
  parseSearchPrefix,
  parseTaxRateInput,
  perRoomFinalEstimateToEstimateDataForUI,
  photoLabelFreeFormId,
  postSlackMessage,
  reconVoiceAgentOriginalVisitPrompt,
  reconcileFormulaUnit,
  requireEnvVar,
  resolvePagePath,
  reverseAuditTrail,
  revertAllDiffsAtAndAfter,
  revertAllDiffsAtAndAfterForList,
  roomsToMappedLineItems,
  selectionsFromSearchResultLabel,
  setFieldByPath,
  simplifyFormulaExpression,
  stripUndefinedFields,
  toAscii,
  transformScopeOfWorkToString,
  tuplesToMappedLineItems,
  unfinishedRoomPrompt,
  updateFormulaIfExceedsMaxAllowedQuantity,
  uploadDraftLineItemDatasetItem,
  validateRuleInputsShape,
  validateUserTypedFormula,
  voiceAgentFollowUpVisitSystemPrompt,
  voiceAgentOriginalVisitPrompt,
  xactimateBadgeFromSearchData
};
//# sourceMappingURL=index.mjs.map